import React, { useState, useEffect, useCallback, useMemo, useReducer } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { PageTransition } from '@/components/PageTransition';
import { useSpaceData } from '@/hooks/useSpaceData';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { supabase } from '@/lib/supabase';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  MouseSensor,
  TouchSensor,
  closestCorners,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus,
  Sparkles,
  Zap,
  User,
  Calendar,
  Lock,
  Link2,
  Settings,
  MoreHorizontal,
  MoreVertical,
  Save,
  Play,
  Eye,
  Copy,
  Trash2,
  FileText,
  Image as ImageIcon,
  File,
  Video,
  Mail,
  Clock,
  Webhook,
  List as ListIcon,
  Code,
  GripVertical,
  Hand,
  PenLine,
  Variable,
  Filter,
  Users,
  FlagTriangleRight,
  Download,
  ExternalLink,
  Loader2,
  Send,
  CheckCircle2,
  XCircle,
  ClipboardCopy,
} from 'lucide-react';
import { WorkflowSetupModal } from './WorkflowSetupModal';
import { TriggerSelectionModal } from './TriggerSelectionModal';
import { AssignmentModal } from './AssignmentModal';
import { DueDateModal } from './DueDateModal';
import { DraggableFieldItem, SortableCanvasItem, DroppableCanvas, DropPlaceholder, AiFlowModal } from './WorkflowBuilderComponents';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

// Animation variants for smooth transitions - optimized to prevent layout warping
const taskItemVariants = {
  initial: { opacity: 0, scale: 0.95, y: -8 },
  animate: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 25
    }
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: 0.15 }
  }
};

// Sortable Task Item Component
function SortableTaskItem({ task, index, isActive, isLast, isFirst, isOnlyOne, onTaskClick, onTaskChange, onTaskFocus, totalTasks, onDuplicate, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: task.id,
    animateLayoutChanges: () => true,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 200ms ease',
    zIndex: isDragging ? 1000 : undefined,
  };

  const isFlowNode = task.type === 'flow';

  // Determine which features are configured
  const hasAssignees = task.assignees?.length > 0;
  const hasDueDate = !!task.dueDate;
  const hasStop = !!task.stopTask;
  const hasConditions = task.conditionalLogic && task.conditionalLogic.mode !== 'disabled';
  const hasAiFlow = task.aiFlowConfig?.enabled && task.aiFlowConfig?.flowId;
  const hasBadges = hasAssignees || hasDueDate || hasStop || hasConditions || hasAiFlow;

  // Feature badges component
  const FeatureBadges = () => {
    if (!hasBadges) return null;
    return (
      <div className="flex items-center gap-0.5 absolute -bottom-2.5 right-3">
        {hasAssignees && (
          <div className="w-5 h-5 rounded-md bg-black flex items-center justify-center" title="Assignees configured">
            <Users className="h-2.5 w-2.5 text-white" />
          </div>
        )}
        {hasDueDate && (
          <div className="w-5 h-5 rounded-md bg-black flex items-center justify-center" title="Due date configured">
            <Clock className="h-2.5 w-2.5 text-white" />
          </div>
        )}
        {hasStop && (
          <div className="w-5 h-5 rounded-md bg-black flex items-center justify-center" title="Stop enabled">
            <Hand className="h-2.5 w-2.5 text-white" />
          </div>
        )}
        {hasConditions && (
          <div className="w-5 h-5 rounded-md bg-black flex items-center justify-center" title="Conditions configured">
            <Filter className="h-2.5 w-2.5 text-white" />
          </div>
        )}
        {hasAiFlow && (
          <div className="w-5 h-5 rounded-md bg-black flex items-center justify-center" title="AI Flow configured">
            <Zap className="h-2.5 w-2.5 text-white" />
          </div>
        )}
      </div>
    );
  };

  // For flow nodes, use a different visual style
  if (isFlowNode) {
    return (
      <div ref={setNodeRef} style={style} className="relative group">
        {/* Flow Node Card */}
        <div
          onClick={onTaskClick}
          className={`relative rounded-xl border-2 bg-card p-3 cursor-pointer transition-all ${isActive
              ? 'border-primary shadow-md'
              : 'border-border hover:border-muted-foreground/50'
            } ${isDragging ? 'shadow-lg opacity-90' : ''}`}
        >
          <div className="flex items-center gap-3">
            {/* Drag Handle with Zap Icon */}
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 cursor-grab active:cursor-grabbing ${isActive
                  ? 'bg-blue-500 text-white'
                  : 'bg-blue-100 text-blue-600'
                }`}
              {...attributes}
              {...listeners}
            >
              <span className="group-hover:hidden">
                <Zap className="h-3.5 w-3.5" />
              </span>
              <GripVertical className="h-3.5 w-3.5 hidden group-hover:block" />
            </div>

            {/* Flow Name Input */}
            <div className="flex-1 min-w-0">
              <Input
                placeholder="Flow name..."
                value={task.name}
                onChange={(e) => {
                  e.stopPropagation();
                  onTaskChange(e.target.value);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onTaskFocus();
                }}
                onFocus={(e) => {
                  e.stopPropagation();
                  onTaskFocus();
                }}
                className="flex-1 h-8 bg-transparent border-0 p-0 text-sm font-medium focus-visible:ring-0 placeholder:text-muted-foreground"
              />
              {task.aiFlowConfig?.flowName && (
                <div className="text-[10px] text-muted-foreground truncate">
                  {task.aiFlowConfig.flowName}
                  {task.aiFlowConfig.mustWait && ' (blocking)'}
                </div>
              )}
            </div>

            {/* More Options */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className="text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onDuplicate(task.id);
                  }}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Duplicate
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(task.id);
                  }}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Feature Badges */}
          <FeatureBadges />
        </div>
      </div>
    );
  }

  // For end screen nodes
  const isEndNode = task.type === 'end';
  if (isEndNode) {
    return (
      <div ref={setNodeRef} style={style} className="relative group">
        <div
          onClick={onTaskClick}
          className={`relative rounded-xl border-2 bg-card p-3 cursor-pointer transition-all ${isActive
              ? 'border-primary shadow-md'
              : 'border-border hover:border-muted-foreground/50'
            } ${isDragging ? 'shadow-lg opacity-90' : ''}`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 cursor-grab active:cursor-grabbing ${isActive
                  ? 'bg-emerald-500 text-white'
                  : 'bg-emerald-100 text-emerald-600'
                }`}
              {...attributes}
              {...listeners}
            >
              <span className="group-hover:hidden">
                <FlagTriangleRight className="h-3.5 w-3.5" />
              </span>
              <GripVertical className="h-3.5 w-3.5 hidden group-hover:block" />
            </div>

            <div className="flex-1 min-w-0">
              <Input
                placeholder="End screen title..."
                value={task.name}
                onChange={(e) => {
                  e.stopPropagation();
                  onTaskChange(e.target.value);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onTaskFocus();
                }}
                onFocus={(e) => {
                  e.stopPropagation();
                  onTaskFocus();
                }}
                className="flex-1 h-8 bg-transparent border-0 p-0 text-sm font-medium focus-visible:ring-0 placeholder:text-muted-foreground"
              />
              {task.actionButtons?.length > 0 && (
                <div className="text-[10px] text-muted-foreground truncate">
                  {task.actionButtons.length} action button{task.actionButtons.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  onClick={(e) => e.stopPropagation()}
                  className="text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(task.id);
                  }}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <FeatureBadges />
        </div>
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={style} className="relative group">
      {/* Task Card */}
      <div
        onClick={onTaskClick}
        className={`relative rounded-xl border-2 bg-card p-3 cursor-pointer transition-all ${isActive
            ? 'border-primary shadow-md'
            : 'border-border hover:border-muted-foreground/50'
          } ${isDragging ? 'shadow-lg opacity-90' : ''}`}
      >
        {/* Drag Handle & Task Number */}
        <div className="flex items-center gap-3">
          <div
            className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 cursor-grab active:cursor-grabbing ${isActive
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground'
              }`}
            {...attributes}
            {...listeners}
          >
            <span className="group-hover:hidden">{index + 1}</span>
            <GripVertical className="h-3.5 w-3.5 hidden group-hover:block" />
          </div>

          {/* Task Input */}
          <Input
            placeholder="Task name..."
            value={task.name}
            onChange={(e) => {
              e.stopPropagation();
              onTaskChange(e.target.value);
            }}
            onClick={(e) => {
              e.stopPropagation();
              onTaskFocus();
            }}
            onFocus={(e) => {
              e.stopPropagation();
              onTaskFocus();
            }}
            className="flex-1 h-8 bg-transparent border-0 p-0 text-sm font-medium focus-visible:ring-0 placeholder:text-muted-foreground/60"
          />

          {/* More Options */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className="text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onDuplicate(task.id);
                }}
              >
                <Copy className="h-4 w-4 mr-2" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(task.id);
                }}
                className="text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Feature Badges */}
        <FeatureBadges />
      </div>
    </div>
  );
}

// Simple spacer between tasks
function TaskSpacer() {
  return <div className="h-3" />;
}

// ============================================================================
// WORKFLOW EDITOR REDUCER - Consolidates 20+ useState calls into single state
// ============================================================================

// Action types for the reducer
const ACTIONS = {
  // UI actions
  SET_SHOW_SETUP_MODAL: 'SET_SHOW_SETUP_MODAL',
  SET_SHOW_TRIGGER_MODAL: 'SET_SHOW_TRIGGER_MODAL',
  SET_IS_EDITING_NAME: 'SET_IS_EDITING_NAME',
  SET_DELETE_DIALOG_OPEN: 'SET_DELETE_DIALOG_OPEN',
  SET_TASK_TO_DELETE: 'SET_TASK_TO_DELETE',
  SET_SHOW_ASSIGNMENT_MODAL: 'SET_SHOW_ASSIGNMENT_MODAL',
  SET_SHOW_DUE_DATE_MODAL: 'SET_SHOW_DUE_DATE_MODAL',
  SET_SHOW_AI_FLOW_MODAL: 'SET_SHOW_AI_FLOW_MODAL',

  // Workflow data actions
  SET_WORKFLOW_NAME: 'SET_WORKFLOW_NAME',
  SET_WORKFLOW_DESCRIPTION: 'SET_WORKFLOW_DESCRIPTION',
  SET_WORKFLOW_SETTINGS: 'SET_WORKFLOW_SETTINGS',
  SET_TRIGGERS: 'SET_TRIGGERS',
  SET_TASKS: 'SET_TASKS',
  UPDATE_TASK: 'UPDATE_TASK',

  // Editor state actions
  SET_ACTIVE_TASK_ID: 'SET_ACTIVE_TASK_ID',
  SET_ACTIVE_DRAG_ITEM: 'SET_ACTIVE_DRAG_ITEM',
  SET_SAVE_STATUS: 'SET_SAVE_STATUS',
  SET_CURRENT_WORKFLOW_ID: 'SET_CURRENT_WORKFLOW_ID',
  SET_IS_LOADING_WORKFLOW: 'SET_IS_LOADING_WORKFLOW',
  SET_HAS_LOADED_INITIAL_DATA: 'SET_HAS_LOADED_INITIAL_DATA',
  SET_WEBHOOK_TOKEN: 'SET_WEBHOOK_TOKEN',

  // Bulk actions
  LOAD_WORKFLOW_DATA: 'LOAD_WORKFLOW_DATA',
  RESET_FOR_NEW_WORKFLOW: 'RESET_FOR_NEW_WORKFLOW',
};

// Initial state factory - creates initial state based on whether we're editing existing workflow
const createInitialState = (effectiveWorkflowId, isCompanyProcessMode) => {
  const defaultProcessName = isCompanyProcessMode ? 'Blank Process' : 'Blank Workflow';

  return {
    // UI state
    ui: {
      showSetupModal: !effectiveWorkflowId,
      showTriggerModal: false,
      isEditingName: false,
      deleteDialogOpen: false,
      taskToDelete: null,
      showAssignmentModal: false,
      showDueDateModal: false,
      showAiFlowModal: false,
    },

    // Workflow data
    workflow: {
      name: defaultProcessName,
      description: '',
      settings: {},
      triggers: [],
      tasks: [
        { id: 'task-initial-1', name: '', content: [] },
      ],
    },

    // Editor state
    editor: {
      activeTaskId: 'task-initial-1',
      activeDragItem: null,
      saveStatus: 'saved', // 'saving' | 'saved' | 'error'
      currentWorkflowId: effectiveWorkflowId,
      isLoadingWorkflow: !!effectiveWorkflowId,
      hasLoadedInitialData: !effectiveWorkflowId,
      webhookToken: null,
    },
  };
};

// Reducer function
function workflowEditorReducer(state, action) {
  switch (action.type) {
    // UI actions
    case ACTIONS.SET_SHOW_SETUP_MODAL:
      return { ...state, ui: { ...state.ui, showSetupModal: action.payload } };
    case ACTIONS.SET_SHOW_TRIGGER_MODAL:
      return { ...state, ui: { ...state.ui, showTriggerModal: action.payload } };
    case ACTIONS.SET_IS_EDITING_NAME:
      return { ...state, ui: { ...state.ui, isEditingName: action.payload } };
    case ACTIONS.SET_DELETE_DIALOG_OPEN:
      return { ...state, ui: { ...state.ui, deleteDialogOpen: action.payload } };
    case ACTIONS.SET_TASK_TO_DELETE:
      return { ...state, ui: { ...state.ui, taskToDelete: action.payload } };
    case ACTIONS.SET_SHOW_ASSIGNMENT_MODAL:
      return { ...state, ui: { ...state.ui, showAssignmentModal: action.payload } };
    case ACTIONS.SET_SHOW_DUE_DATE_MODAL:
      return { ...state, ui: { ...state.ui, showDueDateModal: action.payload } };
    case ACTIONS.SET_SHOW_AI_FLOW_MODAL:
      return { ...state, ui: { ...state.ui, showAiFlowModal: action.payload } };

    // Workflow data actions
    case ACTIONS.SET_WORKFLOW_NAME:
      return { ...state, workflow: { ...state.workflow, name: action.payload } };
    case ACTIONS.SET_WORKFLOW_DESCRIPTION:
      return { ...state, workflow: { ...state.workflow, description: action.payload } };
    case ACTIONS.SET_WORKFLOW_SETTINGS:
      return { ...state, workflow: { ...state.workflow, settings: action.payload } };
    case ACTIONS.SET_TRIGGERS:
      return { ...state, workflow: { ...state.workflow, triggers: action.payload } };
    case ACTIONS.SET_TASKS:
      return { ...state, workflow: { ...state.workflow, tasks: action.payload } };
    case ACTIONS.UPDATE_TASK:
      return {
        ...state,
        workflow: {
          ...state.workflow,
          tasks: state.workflow.tasks.map(task =>
            task.id === action.payload.taskId
              ? { ...task, ...action.payload.updates }
              : task
          ),
        },
      };

    // Editor state actions
    case ACTIONS.SET_ACTIVE_TASK_ID:
      return { ...state, editor: { ...state.editor, activeTaskId: action.payload } };
    case ACTIONS.SET_ACTIVE_DRAG_ITEM:
      return { ...state, editor: { ...state.editor, activeDragItem: action.payload } };
    case ACTIONS.SET_SAVE_STATUS:
      return { ...state, editor: { ...state.editor, saveStatus: action.payload } };
    case ACTIONS.SET_CURRENT_WORKFLOW_ID:
      return { ...state, editor: { ...state.editor, currentWorkflowId: action.payload } };
    case ACTIONS.SET_IS_LOADING_WORKFLOW:
      return { ...state, editor: { ...state.editor, isLoadingWorkflow: action.payload } };
    case ACTIONS.SET_HAS_LOADED_INITIAL_DATA:
      return { ...state, editor: { ...state.editor, hasLoadedInitialData: action.payload } };
    case ACTIONS.SET_WEBHOOK_TOKEN:
      return { ...state, editor: { ...state.editor, webhookToken: action.payload } };

    // Bulk actions for loading workflow data
    case ACTIONS.LOAD_WORKFLOW_DATA:
      return {
        ...state,
        workflow: {
          name: action.payload.name,
          description: action.payload.description || '',
          settings: action.payload.settings || {},
          triggers: action.payload.triggers || [],
          tasks: action.payload.tasks || state.workflow.tasks,
        },
        editor: {
          ...state.editor,
          webhookToken: action.payload.webhookToken,
          isLoadingWorkflow: false,
          hasLoadedInitialData: true,
          activeTaskId: action.payload.tasks?.[0]?.id || state.editor.activeTaskId,
        },
      };

    default:
      console.warn('Unknown action type:', action.type);
      return state;
  }
}

export default function WorkflowEditorPage() {
  const navigate = useNavigate();
  const { projectId, processId } = useParams();
  const [searchParams] = useSearchParams();
  const workflowId = searchParams.get('id');
  const complianceRequirementId = searchParams.get('complianceRequirementId');
  const complianceRequirementName = searchParams.get('name');
  usePageTitle('Workflow Editor');
  const { userProfile, company, can } = useAuth();

  // Detect if we're in company process mode (editing a company-level process)
  const isCompanyProcessMode = window.location.pathname.startsWith('/processes');
  const effectiveWorkflowId = isCompanyProcessMode ? processId : workflowId;

  // Pass company.id to validate the space belongs to the user's current company
  // Skip space data loading in company process mode
  const { space, spaceTools, loading, accessDenied } = useSpaceData(
    isCompanyProcessMode ? null : projectId,
    userProfile?.id,
    company?.id
  );

  // Redirect if user doesn't have access (e.g., switched companies)
  // In company process mode, check for admin permission instead
  useEffect(() => {
    if (isCompanyProcessMode) {
      if (userProfile && company && !can('*')) {
        toast.error('Only administrators can manage company processes');
        navigate('/dashboard', { replace: true });
      }
    } else if (accessDenied) {
      toast.error('You no longer have access to this space');
      navigate('/dashboard', { replace: true });
    }
  }, [accessDenied, navigate, isCompanyProcessMode, userProfile, company, can]);

  // ============================================================================
  // CONSOLIDATED STATE - All component state managed by single useReducer
  // ============================================================================
  const [state, dispatch] = useReducer(
    workflowEditorReducer,
    { effectiveWorkflowId, isCompanyProcessMode },
    ({ effectiveWorkflowId, isCompanyProcessMode }) =>
      createInitialState(effectiveWorkflowId, isCompanyProcessMode)
  );

  // Destructure state for easier access (maintains backward compatibility)
  const { ui, workflow, editor } = state;
  const {
    showSetupModal,
    showTriggerModal,
    isEditingName,
    deleteDialogOpen,
    taskToDelete,
    showAssignmentModal,
    showDueDateModal,
    showAiFlowModal,
  } = ui;
  const {
    name: workflowName,
    description: workflowDescription,
    settings: workflowSettings,
    triggers,
    tasks,
  } = workflow;
  const {
    activeTaskId,
    activeDragItem,
    saveStatus,
    currentWorkflowId,
    isLoadingWorkflow,
    hasLoadedInitialData,
    webhookToken,
  } = editor;

  // Use "Process" terminology when in company process mode
  const defaultProcessName = complianceRequirementName || (isCompanyProcessMode ? 'Blank Process' : 'Blank Workflow');

  // ============================================================================
  // DISPATCH HELPERS - Wrap dispatch calls for cleaner API (backward compatible)
  // ============================================================================
  const setShowSetupModal = useCallback((value) =>
    dispatch({ type: ACTIONS.SET_SHOW_SETUP_MODAL, payload: value }), []);
  const setShowTriggerModal = useCallback((value) =>
    dispatch({ type: ACTIONS.SET_SHOW_TRIGGER_MODAL, payload: value }), []);
  const setIsEditingName = useCallback((value) =>
    dispatch({ type: ACTIONS.SET_IS_EDITING_NAME, payload: value }), []);
  const setDeleteDialogOpen = useCallback((value) =>
    dispatch({ type: ACTIONS.SET_DELETE_DIALOG_OPEN, payload: value }), []);
  const setTaskToDelete = useCallback((value) =>
    dispatch({ type: ACTIONS.SET_TASK_TO_DELETE, payload: value }), []);
  const setShowAssignmentModal = useCallback((value) =>
    dispatch({ type: ACTIONS.SET_SHOW_ASSIGNMENT_MODAL, payload: value }), []);
  const setShowDueDateModal = useCallback((value) =>
    dispatch({ type: ACTIONS.SET_SHOW_DUE_DATE_MODAL, payload: value }), []);
  const setShowAiFlowModal = useCallback((value) =>
    dispatch({ type: ACTIONS.SET_SHOW_AI_FLOW_MODAL, payload: value }), []);

  const setWorkflowName = useCallback((value) =>
    dispatch({ type: ACTIONS.SET_WORKFLOW_NAME, payload: value }), []);
  const setWorkflowDescription = useCallback((value) =>
    dispatch({ type: ACTIONS.SET_WORKFLOW_DESCRIPTION, payload: value }), []);
  const setWorkflowSettings = useCallback((value) =>
    dispatch({ type: ACTIONS.SET_WORKFLOW_SETTINGS, payload: value }), []);
  const setTriggers = useCallback((value) =>
    dispatch({ type: ACTIONS.SET_TRIGGERS, payload: value }), []);
  const setTasks = useCallback((value) =>
    dispatch({ type: ACTIONS.SET_TASKS, payload: value }), []);

  const setActiveTaskId = useCallback((value) =>
    dispatch({ type: ACTIONS.SET_ACTIVE_TASK_ID, payload: value }), []);
  const setActiveDragItem = useCallback((value) =>
    dispatch({ type: ACTIONS.SET_ACTIVE_DRAG_ITEM, payload: value }), []);
  const setSaveStatus = useCallback((value) =>
    dispatch({ type: ACTIONS.SET_SAVE_STATUS, payload: value }), []);
  const setCurrentWorkflowId = useCallback((value) =>
    dispatch({ type: ACTIONS.SET_CURRENT_WORKFLOW_ID, payload: value }), []);
  const setIsLoadingWorkflow = useCallback((value) =>
    dispatch({ type: ACTIONS.SET_IS_LOADING_WORKFLOW, payload: value }), []);
  const setHasLoadedInitialData = useCallback((value) =>
    dispatch({ type: ACTIONS.SET_HAS_LOADED_INITIAL_DATA, payload: value }), []);
  const setWebhookToken = useCallback((value) =>
    dispatch({ type: ACTIONS.SET_WEBHOOK_TOKEN, payload: value }), []);

  // Bulk action for loading workflow data
  const loadWorkflowData = useCallback((data) =>
    dispatch({ type: ACTIONS.LOAD_WORKFLOW_DATA, payload: data }), []);

  // Sidebar tab state - 'content' or 'variables'
  const [sidebarTab, setSidebarTab] = useState('content');

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 6,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Trigger icon mapping - must match TriggerSelectionModal
  const triggerIconMap = {
    'manual': Play,
    'email': Mail,
    'schedule': Clock,
    'webhook': Webhook,
  };

  // Restore trigger icons from saved data
  const restoreTriggerIcons = (savedTriggers) => {
    if (!savedTriggers) return [];
    return savedTriggers.map(trigger => ({
      ...trigger,
      icon: triggerIconMap[trigger.id] || Play
    }));
  };

  // Load existing workflow
  // Note: Setter functions are stable (memoized with useCallback) and don't need to be in deps
  useEffect(() => {
    const loadWorkflowAsync = async () => {
      if (!effectiveWorkflowId) {
        setIsLoadingWorkflow(false);
        setHasLoadedInitialData(true);
        return;
      }

      // Wait for company data in company process mode - don't set loading false yet
      if (isCompanyProcessMode && !company?.id) {
        return; // Keep isLoadingWorkflow true, wait for company data
      }

      try {
        let data;

        if (isCompanyProcessMode) {
          // Company processes: use RPC for proper permission check
          const { data: rpcData, error } = await supabase.rpc('get_company_process', {
            p_workflow_id: effectiveWorkflowId,
            p_company_id: company.id
          });
          if (error) throw error;
          data = rpcData?.[0];
        } else {
          // Space workflows: direct query with RLS
          const { data: queryData, error } = await supabase
            .from('workflows')
            .select('*')
            .eq('id', effectiveWorkflowId)
            .single();
          if (error) throw error;
          data = queryData;
        }

        if (data) {
          let loadedTasks = data.tasks || [{ id: 'task-initial-1', name: '', content: [] }];

          // Fix duplicate IDs if they exist (legacy data fix)
          const seenIds = new Set();
          let hasDuplicates = false;
          loadedTasks = loadedTasks.map((task, index) => {
            if (seenIds.has(task.id)) {
              hasDuplicates = true;
              // Generate a new unique ID for duplicate
              const newId = `task-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${index}`;
              console.warn(`Fixed duplicate task ID: ${task.id} -> ${newId}`);
              return { ...task, id: newId };
            }
            seenIds.add(task.id);
            return task;
          });

          if (hasDuplicates) {
            toast.info('Fixed duplicate task IDs in this workflow');
          }

          // Use bulk action to update all workflow data at once (reduces re-renders)
          loadWorkflowData({
            name: data.name || defaultProcessName,
            description: data.description || '',
            settings: data.settings || {},
            triggers: restoreTriggerIcons(data.triggers),
            tasks: loadedTasks,
            webhookToken: data.webhook_token,
          });

          setCurrentWorkflowId(data.id);
        } else {
          // Workflow not found - redirect back
          toast.error(isCompanyProcessMode ? 'Process not found' : 'Workflow not found');
          navigate(isCompanyProcessMode ? '/processes' : `/space/${projectId}/tasks`);
          return;
        }
      } catch (error) {
        console.error('Error loading workflow:', error);
        toast.error(isCompanyProcessMode ? 'Failed to load process' : 'Failed to load workflow');
        // Redirect back on error - don't leave user in broken state
        navigate(isCompanyProcessMode ? '/processes' : `/space/${projectId}/tasks`);
      } finally {
        setIsLoadingWorkflow(false);
      }
    };

    loadWorkflowAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveWorkflowId, isCompanyProcessMode, company?.id, navigate, projectId]);

  // Save workflow to database
  const saveWorkflow = useCallback(async () => {
    // In company process mode, we need company.id; in space mode, we need projectId
    if (isCompanyProcessMode) {
      if (!company?.id || !userProfile?.id) return;
    } else {
      if (!projectId || !userProfile?.id) return;
    }

    setSaveStatus('saving');

    try {
      // Remove icon components from tasks before saving (can't serialize React components)
      const tasksToSave = tasks.map(task => ({
        ...task,
        content: task.content.map(item => {
          const { icon, ...itemWithoutIcon } = item;
          return itemWithoutIcon;
        })
      }));

      // Check if webhook trigger is enabled
      const hasWebhookTrigger = triggers.some(t => t.id === 'webhook');

      // Build workflow data based on mode
      const workflowData = isCompanyProcessMode
        ? {
          name: workflowName,
          company_id: company.id,
          project_id: null,
          is_company_process: true,
          triggers: triggers,
          tasks: tasksToSave,
          settings: workflowSettings,
          status: 'draft',
          webhook_enabled: hasWebhookTrigger,
        }
        : {
          name: workflowName,
          project_id: projectId,
          triggers: triggers,
          tasks: tasksToSave,
          settings: workflowSettings,
          status: 'draft',
          webhook_enabled: hasWebhookTrigger,
        };

      if (currentWorkflowId) {
        // Update existing workflow - use RPC for company processes
        if (isCompanyProcessMode) {
          const { error } = await supabase.rpc('update_company_process', {
            p_workflow_id: currentWorkflowId,
            p_company_id: company.id,
            p_name: workflowName,
            p_settings: workflowSettings,
            p_triggers: triggers,
            p_tasks: tasksToSave,
            p_webhook_enabled: hasWebhookTrigger
          });

          if (error) throw error;
        } else {
          const { error } = await supabase
            .from('workflows')
            .update(workflowData)
            .eq('id', currentWorkflowId);

          if (error) throw error;
        }
      } else {
        // Create new workflow - use RPC for company processes
        let data;

        if (isCompanyProcessMode) {
          const { data: rpcData, error } = await supabase.rpc('create_company_process', {
            p_company_id: company.id,
            p_name: workflowName,
            p_description: '',
            p_settings: workflowSettings,
            p_triggers: triggers,
            p_tasks: tasksToSave
          });

          if (error) throw error;
          data = rpcData?.[0];
        } else {
          const { data: insertData, error } = await supabase
            .from('workflows')
            .insert({
              ...workflowData,
              created_by: userProfile.id,
            })
            .select()
            .single();

          if (error) throw error;
          data = insertData;
        }

        if (data) {
          setCurrentWorkflowId(data.id);
          setWebhookToken(data.webhook_token);
          // Update URL with workflow ID - different routes for company vs space
          if (isCompanyProcessMode) {
            navigate(`/processes/editor/${data.id}`, { replace: true });
          } else {
            navigate(`/space/${projectId}/workflow-editor?id=${data.id}`, { replace: true });
          }
        }
      }

      setSaveStatus('saved');
    } catch (error) {
      console.error('Error saving workflow:', error);
      setSaveStatus('error');
      toast.error('Failed to save workflow');
      setTimeout(() => setSaveStatus('saved'), 3000);
    }
  }, [projectId, userProfile, company, workflowName, triggers, tasks, workflowSettings, currentWorkflowId, navigate, isCompanyProcessMode]);

  // OPTIMIZED: Serialize workflow content for change detection
  // This prevents auto-save from triggering on every render due to object reference changes
  const workflowContentHash = useMemo(() =>
    JSON.stringify({ name: workflowName, triggers, tasks, settings: workflowSettings })
    , [workflowName, triggers, tasks, workflowSettings]);

  // Auto-save when data changes (debounced)
  // Don't auto-save if:
  // 1. Still loading the workflow
  // 2. Initial data hasn't been loaded yet (prevents overwriting with stale defaults)
  // 3. Setup modal is open and no workflow created yet (user hasn't completed initial setup)
  useEffect(() => {
    if (isLoadingWorkflow || !hasLoadedInitialData) return;

    // Don't auto-save until user completes the setup modal for new workflows
    if (showSetupModal && !currentWorkflowId) return;

    const timeoutId = setTimeout(() => {
      saveWorkflow();
    }, 1500); // Increased to 1.5 seconds to reduce save frequency

    return () => clearTimeout(timeoutId);
  }, [workflowContentHash, saveWorkflow, isLoadingWorkflow, hasLoadedInitialData, showSetupModal, currentWorkflowId]);

  const handleSetupSave = async (setupData) => {
    try {
      const settingsData = {
        runName: setupData.runName,
        dueDate: setupData.dueDate,
        runComments: setupData.runComments,
        shareLinks: setupData.shareLinks,
        showSetupAtStart: setupData.showSetupAtStart,
        background: setupData.background,
        notificationCover: setupData.notificationCover,
        icon: setupData.icon
      };

      if (currentWorkflowId) {
        // Update existing workflow
        const { error } = await supabase
          .from('workflows')
          .update({
            name: setupData.workflowName,
            description: setupData.description || '',
            settings: settingsData,
          })
          .eq('id', currentWorkflowId);

        if (error) throw error;

        // Update local state
        setWorkflowName(setupData.workflowName);
        setWorkflowSettings(settingsData);
        setShowSetupModal(false);

        toast.success(isCompanyProcessMode ? 'Process settings updated!' : 'Workflow settings updated!');
      } else {
        // Create new workflow - use RPC for company processes to bypass RLS
        let workflow;

        // Include the initial tasks from local state so the first page exists immediately
        const initialTasks = tasks.length > 0 ? tasks.map(task => {
          const { icon, ...rest } = task;
          return { ...rest, content: (task.content || []).map(({ icon: _icon, ...item }) => item) };
        }) : [{ id: 'task-initial-1', name: '', content: [] }];

        if (isCompanyProcessMode) {
          // Use RPC function for company processes
          const { data, error } = await supabase.rpc('create_company_process', {
            p_company_id: company.id,
            p_name: setupData.workflowName,
            p_description: setupData.description || '',
            p_settings: settingsData,
            p_triggers: [],
            p_tasks: initialTasks
          });

          if (error) throw error;
          workflow = data?.[0];
        } else {
          // Direct insert for space workflows
          const { data, error } = await supabase
            .from('workflows')
            .insert({
              project_id: projectId,
              name: setupData.workflowName,
              description: setupData.description || '',
              settings: settingsData,
              status: 'draft',
              created_by: userProfile?.id,
              triggers: [],
              tasks: initialTasks,
              compliance_requirement_id: complianceRequirementId || null
            })
            .select()
            .single();

          if (error) throw error;
          workflow = data;
        }

        // Update local state
        setWorkflowName(setupData.workflowName);
        setWorkflowSettings(settingsData);
        setCurrentWorkflowId(workflow.id);
        setShowSetupModal(false);

        toast.success(isCompanyProcessMode ? 'Process created successfully!' : 'Workflow created successfully!');

        // Update URL with workflow ID so we can edit it
        if (isCompanyProcessMode) {
          navigate(`/processes/editor/${workflow.id}`, { replace: true });
        } else {
          navigate(`/space/${projectId}/workflow-editor?id=${workflow.id}`, { replace: true });
        }
      }
    } catch (error) {
      console.error('Error saving workflow:', error);
      toast.error(isCompanyProcessMode ? 'Failed to save process. Please try again.' : 'Failed to save workflow. Please try again.');
    }
  };

  const handleTriggerSelect = (trigger) => {
    const exists = triggers.find(t => t.id === trigger.id);
    if (exists) {
      // Remove trigger if already selected
      setTriggers(triggers.filter(t => t.id !== trigger.id));
    } else {
      // Add trigger if not selected
      setTriggers([...triggers, trigger]);
    }
  };

  const handleSaveAssignees = (assignees) => {
    const updatedTasks = tasks.map(task =>
      task.id === activeTaskId
        ? { ...task, assignees }
        : task
    );
    setTasks(updatedTasks);
    toast.success('Assignees updated');
  };

  const handleSaveDueDate = (dueDate) => {
    const updatedTasks = tasks.map(task =>
      task.id === activeTaskId
        ? { ...task, dueDate }
        : task
    );
    setTasks(updatedTasks);
    if (dueDate) {
      toast.success('Due date updated');
    } else {
      toast.success('Due date removed');
    }
  };

  const handlePublishWorkflow = async () => {
    if (!currentWorkflowId) {
      toast.error('Please save the workflow first');
      return;
    }

    // Validate context based on mode
    if (isCompanyProcessMode) {
      if (!company?.id) {
        toast.error('Company context required');
        return;
      }
    } else {
      if (!projectId) {
        toast.error('Please save the workflow first');
        return;
      }
    }

    try {
      // Check if webhook trigger is enabled
      const hasWebhookTrigger = triggers.some(t => t.id === 'webhook');

      // Update workflow status to published
      if (isCompanyProcessMode) {
        // Use RPC for company processes (bypasses RLS)
        const { error } = await supabase.rpc('update_company_process', {
          p_workflow_id: currentWorkflowId,
          p_company_id: company.id,
          p_webhook_enabled: hasWebhookTrigger,
          p_status: 'published'
        });

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('workflows')
          .update({
            status: 'published',
            updated_date: new Date().toISOString(),
            webhook_enabled: hasWebhookTrigger,
          })
          .eq('id', currentWorkflowId);

        if (error) throw error;
      }

      toast.success(isCompanyProcessMode ? 'Process published!' : 'Workflow published!');

      // Navigate to the management page
      if (isCompanyProcessMode) {
        navigate(`/processes/${currentWorkflowId}`);
      } else {
        navigate(`/space/${projectId}/library/${currentWorkflowId}`);
      }
    } catch (error) {
      console.error('Error publishing workflow:', error);
      toast.error('Failed to publish');
    }
  };

  const handlePublishAndRun = async () => {
    // For company processes, we can't run directly - need to go to a space first
    if (isCompanyProcessMode) {
      await handlePublishWorkflow();
      toast.info('Share this process with spaces to run it');
      return;
    }

    if (!currentWorkflowId || !projectId) {
      toast.error('Please save the workflow first');
      return;
    }

    try {
      // Check if webhook trigger is enabled
      const hasWebhookTrigger = triggers.some(t => t.id === 'webhook');

      // First publish the workflow
      const { error: publishError } = await supabase
        .from('workflows')
        .update({
          status: 'published',
          updated_date: new Date().toISOString(),
          webhook_enabled: hasWebhookTrigger,
        })
        .eq('id', currentWorkflowId);

      if (publishError) throw publishError;

      // Parse run name template
      const runNameTemplate = workflowSettings.runName || '{{ current_user.name }} - {{ current_date.full }}';
      const variables = {
        'current_user.name': `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`.trim() || 'Unknown User',
        'current_user.email': userProfile?.email || '',
        'current_user.first_name': userProfile?.first_name || '',
        'current_date.full': new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        'current_date.short': new Date().toLocaleDateString('en-US'),
        'current_date.iso': new Date().toISOString().split('T')[0],
        'current_time': new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
        'workflow.name': workflowName,
        'run_number': '1', // Will be calculated properly
      };

      const parsedRunName = runNameTemplate.replace(/\{\{\s*([^}]+)\s*\}\}/g, (match, key) => {
        const trimmedKey = key.trim();
        return variables[trimmedKey] !== undefined ? variables[trimmedKey] : match;
      });

      // Create a new workflow run
      const { data: run, error: runError } = await supabase
        .from('checklists_workflow_runs')
        .insert({
          workflow_id: currentWorkflowId,
          project_id: projectId,
          run_name: parsedRunName,
          status: 'in_progress',
          started_by: userProfile?.id,
          started_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (runError) throw runError;

      // Create task runs only up to and including the first task with stopTask enabled
      // This allows the workflow to pause at stop points
      const taskRunsToCreate = [];
      for (let i = 0; i < tasks.length; i++) {
        const task = tasks[i];
        taskRunsToCreate.push({
          workflow_run_id: run.id,
          task_id: String(task.id),
          task_order: i,
          status: i === 0 ? 'in_progress' : 'pending',
          assignees: task.assignees || [],
          due_date: task.dueDate ? calculateDueDate(task.dueDate) : null,
        });

        // If this task has stopTask enabled, don't create any more task runs
        if (task.stopTask) {
          break;
        }
      }

      if (taskRunsToCreate.length > 0) {
        const { error: taskRunsError } = await supabase
          .from('checklists_workflow_task_runs')
          .insert(taskRunsToCreate);

        if (taskRunsError) throw taskRunsError;
      }

      toast.success('Workflow published and run started!');

      // Navigate to the workflow run page
      navigate(`/space/${projectId}/workflow-run/${run.id}`);
    } catch (error) {
      console.error('Error publishing and running workflow:', error);
      toast.error('Failed to publish and run workflow');
    }
  };

  const handleDeleteWorkflow = async () => {
    const backPath = isCompanyProcessMode ? '/processes' : `/space/${projectId}/tasks`;

    if (!currentWorkflowId) {
      // If no workflow saved yet, just navigate back
      navigate(backPath);
      return;
    }

    if (!window.confirm(`Are you sure you want to delete this ${isCompanyProcessMode ? 'process' : 'workflow'}? This action cannot be undone.`)) {
      return;
    }

    try {
      if (isCompanyProcessMode) {
        // Company processes: use RPC
        const { error } = await supabase.rpc('delete_company_process', {
          p_workflow_id: currentWorkflowId,
          p_company_id: company.id
        });
        if (error) throw error;
      } else {
        // Space workflows: direct delete with RLS
        const { error } = await supabase
          .from('workflows')
          .delete()
          .eq('id', currentWorkflowId);
        if (error) throw error;
      }

      toast.success(isCompanyProcessMode ? 'Process deleted' : 'Workflow deleted');
      navigate(backPath);
    } catch (error) {
      console.error('Error deleting workflow:', error);
      toast.error('Failed to delete');
    }
  };

  // Helper to calculate due date based on task settings
  const calculateDueDate = (dueDateConfig) => {
    if (!dueDateConfig) return null;

    const now = new Date();
    let dueDate = new Date(now);

    // Add time based on config
    if (dueDateConfig.months) dueDate.setMonth(dueDate.getMonth() + dueDateConfig.months);
    if (dueDateConfig.days) dueDate.setDate(dueDate.getDate() + dueDateConfig.days);
    if (dueDateConfig.hours) dueDate.setHours(dueDate.getHours() + dueDateConfig.hours);
    if (dueDateConfig.minutes) dueDate.setMinutes(dueDate.getMinutes() + dueDateConfig.minutes);

    // Handle business day logic
    if (dueDateConfig.businessDayHandling === 'shift_next') {
      const day = dueDate.getDay();
      if (day === 0) dueDate.setDate(dueDate.getDate() + 1); // Sunday -> Monday
      if (day === 6) dueDate.setDate(dueDate.getDate() + 2); // Saturday -> Monday
    } else if (dueDateConfig.businessDayHandling === 'business_only') {
      const day = dueDate.getDay();
      if (day === 0) dueDate.setDate(dueDate.getDate() + 1);
      if (day === 6) dueDate.setDate(dueDate.getDate() + 2);
    }

    return dueDate.toISOString();
  };

  const addTask = (type = 'task') => {
    // Generate unique ID using timestamp + random to avoid collisions
    const newTaskId = `task-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newTask = {
      id: newTaskId,
      name: type === 'end' ? 'End Screen' : '',
      formTitle: type === 'end' ? 'End Screen' : '',
      type,
      content: [],
      ...(type === 'end' ? { actionButtons: [] } : {}),
    };

    // End screens must always be last. Insert before end screen if one exists.
    if (type !== 'end' && hasEndScreen) {
      const newTasks = [...tasks];
      newTasks.splice(tasks.length - 1, 0, newTask);
      setTasks(newTasks);
    } else {
      setTasks([...tasks, newTask]);
    }
    setActiveTaskId(newTaskId);
  };

  const [dragOverItemId, setDragOverItemId] = useState(null);
  const [dragOverPosition, setDragOverPosition] = useState('below'); // 'above' or 'below'
  const [activeTaskDragId, setActiveTaskDragId] = useState(null);
  const [overTaskId, setOverTaskId] = useState(null);

  const handleDragStart = (event) => {
    setActiveDragItem(event.active.data.current);
    // Check if we're dragging a task from the sidebar
    if (tasks.some(t => t.id === event.active.id)) {
      setActiveTaskDragId(event.active.id);
    }
  };

  const handleDragOver = (event) => {
    const { over, active } = event;

    // Track which task we're hovering over for reordering
    if (active && tasks.some(t => t.id === active.id) && over && tasks.some(t => t.id === over.id)) {
      setOverTaskId(over.id);
    } else {
      setOverTaskId(null);
    }

    // Track which item we're hovering over for visual feedback
    if (over?.data?.current?.type === 'canvas-item') {
      setDragOverItemId(over.id);
      // Determine if pointer is in top or bottom half of hovered item
      const activeTranslated = active?.rect?.current?.translated;
      if (over.rect && activeTranslated) {
        const activeCenterY = activeTranslated.top + activeTranslated.height / 2;
        const overMidpointY = over.rect.top + over.rect.height / 2;
        setDragOverPosition(activeCenterY < overMidpointY ? 'above' : 'below');
      }
    } else {
      setDragOverItemId(null);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over) {
      setActiveDragItem(null);
      setDragOverItemId(null);
      setDragOverPosition('below');
      setActiveTaskDragId(null);
      setOverTaskId(null);
      return;
    }

    // Check if we're reordering tasks in the left sidebar
    const isReorderingTasks = tasks.some(t => t.id === active.id) && tasks.some(t => t.id === over.id);
    if (isReorderingTasks) {
      const oldIndex = tasks.findIndex(t => t.id === active.id);
      let newIndex = tasks.findIndex(t => t.id === over.id);

      // Enforce: end screen must always stay at the last position
      const draggedTask = tasks[oldIndex];
      const lastTask = tasks[tasks.length - 1];
      if (draggedTask?.type === 'end') {
        // Don't allow moving end screen away from last position
        newIndex = tasks.length - 1;
      } else if (lastTask?.type === 'end' && newIndex >= tasks.length - 1) {
        // Don't allow moving other tasks past the end screen
        newIndex = tasks.length - 2;
      }

      if (oldIndex !== newIndex && newIndex >= 0) {
        setTasks(arrayMove(tasks, oldIndex, newIndex));
      }

      setActiveDragItem(null);
      setDragOverItemId(null);
      setActiveTaskDragId(null);
      setOverTaskId(null);
      return;
    }

    const activeTask = tasks.find(t => t.id === activeTaskId);
    if (!activeTask) return;

    // Adding new item from sidebar - only if dropped on the canvas area
    if (active.data.current?.type === 'new-item') {
      const isOverCanvas = over.data.current?.type === 'canvas-item' || over.id === 'canvas-droppable';

      if (isOverCanvas) {
        const newItem = {
          id: `${active.data.current.itemType}-${Date.now()}`,
          type: active.data.current.itemType,
          label: active.data.current.label,
          icon: active.data.current.icon,
          color: active.data.current.color,
          category: active.data.current.category,
        };

        // Dropdown gets default placeholder options
        if (newItem.label === 'Dropdown') {
          newItem.content = {
            dataSourceType: 'static',
            options: [
              { id: Date.now(), value: 'Option 1' },
              { id: Date.now() + 1, value: 'Option 2' },
              { id: Date.now() + 2, value: 'Option 3' },
            ],
          };
        }

        let insertIndex = activeTask.content.length; // Default to end

        // If dropped over a canvas item, insert before/after it based on pointer position
        if (over.data.current?.type === 'canvas-item') {
          const overIndex = activeTask.content.findIndex(item => item.id === over.id);
          if (overIndex !== -1) {
            insertIndex = dragOverPosition === 'above' ? overIndex : overIndex + 1;
          }
        }

        const newContent = [...activeTask.content];
        newContent.splice(insertIndex, 0, newItem);

        setTasks(tasks.map(task =>
          task.id === activeTaskId
            ? { ...task, content: newContent }
            : task
        ));
      }
    }

    // Reordering existing items
    if (active.data.current?.type === 'canvas-item' && over.data.current?.type === 'canvas-item') {

      const oldIndex = activeTask.content.findIndex(item => item.id === active.id);
      const newIndex = activeTask.content.findIndex(item => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newContent = arrayMove(activeTask.content, oldIndex, newIndex);
        setTasks(tasks.map(task =>
          task.id === activeTaskId
            ? { ...task, content: newContent }
            : task
        ));
      }
    }

    setActiveDragItem(null);
    setDragOverItemId(null);
    setDragOverPosition('below');
  };

  const handleDragCancel = () => {
    setActiveDragItem(null);
    setDragOverItemId(null);
    setDragOverPosition('below');
    setActiveTaskDragId(null);
    setOverTaskId(null);
  };

  const removeContentItem = (itemId) => {
    setTasks(tasks.map(task =>
      task.id === activeTaskId
        ? { ...task, content: task.content.filter(item => item.id !== itemId) }
        : task
    ));
  };

  const handleDuplicateTask = (taskId) => {
    const taskToDuplicate = tasks.find(t => t.id === taskId);
    if (!taskToDuplicate) return;

    // Create a deep copy of the task with a unique ID
    const newTaskId = `task-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const duplicatedTask = JSON.parse(JSON.stringify(taskToDuplicate));
    duplicatedTask.id = newTaskId;
    duplicatedTask.name = `${duplicatedTask.name || ''} (Copy)`;
    duplicatedTask.formTitle = `${duplicatedTask.formTitle || ''} (Copy)`;

    // Insert the duplicated task right after the original
    const originalIndex = tasks.findIndex(t => t.id === taskId);
    const newTasks = [...tasks];
    newTasks.splice(originalIndex + 1, 0, duplicatedTask);
    setTasks(newTasks);

    toast.success('Task duplicated');
  };

  const handleDeleteTask = (taskId) => {
    setTaskToDelete(taskId);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteTask = () => {
    if (!taskToDelete) return;

    // Don't allow deleting if it's the only task
    if (tasks.length === 1) {
      toast.error('Cannot delete the only task');
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
      return;
    }

    // Remove the task
    const newTasks = tasks.filter(t => t.id !== taskToDelete);
    setTasks(newTasks);

    // If the deleted task was active, switch to the first task
    if (activeTaskId === taskToDelete) {
      setActiveTaskId(newTasks[0].id);
    }

    toast.success('Task deleted');
    setDeleteDialogOpen(false);
    setTaskToDelete(null);
  };

  const duplicateContentItem = (itemId) => {
    setTasks(tasks.map(task => {
      if (task.id === activeTaskId) {
        const itemToDuplicate = task.content.find(item => item.id === itemId);
        if (!itemToDuplicate) return task;

        // Create a deep copy of the item with a new unique ID
        // Use JSON parse/stringify for deep copy to preserve all nested content
        const duplicatedItem = JSON.parse(JSON.stringify(itemToDuplicate));
        duplicatedItem.id = `${itemToDuplicate.type}-${Date.now()}`;

        // Find the index of the original item and insert duplicate right after it
        const originalIndex = task.content.findIndex(item => item.id === itemId);
        const newContent = [...task.content];
        newContent.splice(originalIndex + 1, 0, duplicatedItem);

        return { ...task, content: newContent };
      }
      return task;
    }));
  };

  const updateFormTitle = (newTitle) => {
    setTasks(tasks.map(task =>
      task.id === activeTaskId
        ? { ...task, formTitle: newTitle, name: newTitle }
        : task
    ));
  };

  const updateContentItem = (itemId, newContent) => {
    setTasks(tasks.map(task =>
      task.id === activeTaskId
        ? {
          ...task,
          content: task.content.map(item =>
            item.id === itemId ? { ...item, content: newContent } : item
          )
        }
        : task
    ));
  };

  // Update conditional logic for a content item
  const updateConditionalLogic = (itemId, conditionalLogic) => {
    setTasks(tasks.map(task =>
      task.id === activeTaskId
        ? {
          ...task,
          content: task.content.map(item =>
            item.id === itemId ? { ...item, conditionalLogic } : item
          )
        }
        : task
    ));
  };

  // Update task-level conditional logic
  const updateTaskConditionalLogic = (conditionalLogic) => {
    setTasks(tasks.map(task =>
      task.id === activeTaskId
        ? { ...task, conditionalLogic }
        : task
    ));
  };

  const contentItems = [
    { icon: FileText, label: 'Text', color: 'text-gray-600' },
    { icon: ImageIcon, label: 'Image', color: 'text-gray-600' },
    { icon: File, label: 'File', color: 'text-gray-600' },
    { icon: Video, label: 'Video', color: 'text-gray-600' },
    { icon: FileText, label: 'Page', color: 'text-gray-600' },
    // { icon: Mail, label: 'Send Email', color: 'text-gray-600' },
    { icon: ListIcon, label: 'Subtasks', color: 'text-gray-600' },
    { icon: Code, label: 'Embed', color: 'text-gray-600' },
  ];

  const formFields = [
    { icon: FileText, label: 'Short Text', color: 'text-orange-500' },
    { icon: FileText, label: 'Long Text', color: 'text-orange-500' },
    { icon: Mail, label: 'Email', color: 'text-orange-500' },
    { icon: Link2, label: 'Website', color: 'text-orange-500' },
    { icon: File, label: 'File Upload', color: 'text-orange-500' },
    { icon: Calendar, label: 'Date', color: 'text-orange-500' },
    { icon: FileText, label: 'Numbers', color: 'text-orange-500' },
    { icon: ListIcon, label: 'Dropdown', color: 'text-orange-500' },
    { icon: ListIcon, label: 'Multi Choice', color: 'text-orange-500' },
    { icon: User, label: 'User', color: 'text-orange-500' },
    { icon: FileText, label: 'Table', color: 'text-orange-500' },
    { icon: PenLine, label: 'Signature', color: 'text-orange-500' },
    // { icon: FileText, label: 'Snippet', color: 'text-orange-500' },
    // { icon: FileText, label: 'Hidden', color: 'text-orange-500' },
  ];

  // Compute available variables from form fields and flow outputs in previous tasks
  const availableVariables = useMemo(() => {
    const variables = [];

    // Add webhook variables (available on all tasks)
    const webhookVars = workflowSettings?.webhookVariables || [];
    webhookVars.forEach(v => {
      variables.push({
        id: `webhook-var-${v.key}`,
        fieldName: v.key.replace('webhook.', ''),
        fieldType: 'Webhook',
        taskName: 'Webhook Payload',
        taskIndex: 0,
        variableKey: `{{ ${v.key} }}`,
        payloadKey: v.key,
        isWebhook: true,
        sampleValue: v.sampleValue,
      });
    });

    if (!tasks || !activeTaskId) return variables;

    const currentIndex = tasks.findIndex(t => String(t.id) === String(activeTaskId));
    if (currentIndex <= 0) return variables; // No previous tasks, but may have webhook vars

    const slugCounts = {};
    const toSlug = (name) => {
      if (!name) return '';
      return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    };

    // Iterate through tasks before current one
    for (let i = 0; i < currentIndex; i++) {
      const task = tasks[i];
      const taskName = task.formTitle || task.name || `Step ${i + 1}`;

      // Get form fields from this task
      (task.content || [])
        .filter(item => item.category === 'form')
        .forEach(item => {
          const fieldName = item.content?.fieldName || item.label;
          let slug = toSlug(fieldName);
          if (!slug) slug = item.id;
          if (slugCounts[slug]) {
            slugCounts[slug]++;
            slug = `${slug}_${slugCounts[slug]}`;
          } else {
            slugCounts[slug] = 1;
          }

          variables.push({
            id: item.id,
            fieldName,
            fieldType: item.label,
            taskName,
            taskIndex: i + 1,
            variableKey: `{{ ${slug} }}`,
            payloadKey: slug,
          });
        });

      // Get flow output variables from flow nodes
      if (task.type === 'flow' && task.aiFlowConfig?.expectedOutputVars?.length > 0) {
        task.aiFlowConfig.expectedOutputVars.forEach(v => {
          variables.push({
            id: `flow-var-${v.key}`,
            fieldName: v.key,
            fieldType: 'Flow Output',
            taskName,
            taskIndex: i + 1,
            variableKey: `{{ ${v.key} }}`,
            payloadKey: v.key,
            isFlowOutput: true,
          });
        });
      }
    }

    return variables;
  }, [tasks, activeTaskId, workflowSettings?.webhookVariables]);

  // MEMOIZED: Active task and derived values - only recalculates when tasks or activeTaskId change
  const activeTask = useMemo(() =>
    tasks.find(t => t.id === activeTaskId)
    , [tasks, activeTaskId]);

  const { activeTaskContent, assigneeCount, hasDueDate, hasStopEnabled, hasAiFlow, isActiveTaskFlow, isActiveTaskEnd } = useMemo(() => ({
    activeTaskContent: activeTask?.content || [],
    assigneeCount: activeTask?.assignees?.length || 0,
    hasDueDate: !!activeTask?.dueDate,
    hasStopEnabled: !!activeTask?.stopTask,
    hasAiFlow: !!activeTask?.aiFlowConfig?.flowId && activeTask?.aiFlowConfig?.enabled !== false,
    isActiveTaskFlow: activeTask?.type === 'flow',
    isActiveTaskEnd: activeTask?.type === 'end'
  }), [activeTask]);

  // Check if the last task is an end screen (to prevent adding nodes below it)
  const hasEndScreen = tasks.length > 0 && tasks[tasks.length - 1]?.type === 'end';

  // Handle toggling the stop setting for a task
  const handleToggleStop = () => {
    const updatedTasks = tasks.map(task =>
      task.id === activeTaskId
        ? { ...task, stopTask: !task.stopTask }
        : task
    );
    setTasks(updatedTasks);
    if (!activeTask?.stopTask) {
      toast.success('Stop enabled: Workflow will pause at this task until completed');
    } else {
      toast.success('Stop disabled: Workflow will continue automatically');
    }
  };

  // Handle saving AI flow config for a task
  const handleSaveAiFlowConfig = (aiFlowConfig) => {
    const updatedTasks = tasks.map(task =>
      task.id === activeTaskId
        ? { ...task, aiFlowConfig }
        : task
    );
    setTasks(updatedTasks);
    if (aiFlowConfig?.flowId) {
      toast.success(`AI Flow "${aiFlowConfig.flowName}" configured for this page`);
    } else {
      toast.success('AI Flow removed from this page');
    }
  };

  // --- Test Flow ---
  const [showTestFlowDialog, setShowTestFlowDialog] = useState(false);
  const [testPayload, setTestPayload] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testStatus, setTestStatus] = useState('idle'); // idle | sending | waiting | error | webhook_received
  const [testWebhookToken, setTestWebhookToken] = useState(null);
  const [testWebhookData, setTestWebhookData] = useState(null);

  const SUPABASE_URL = 'https://yqmlqctpakqxtwzrdofl.supabase.co';

  // Realtime subscription for test webhook results - listens for the callback
  useEffect(() => {
    if (!showTestFlowDialog || !testWebhookToken || testStatus !== 'waiting') return;

    const channel = supabase
      .channel(`webhook-test-${testWebhookToken}`)
      .on('broadcast', { event: 'webhook_callback' }, (payload) => {
        console.log('[Test Webhook] Broadcast received:', payload);
        const data = payload.payload?.data;
        if (data) {
          setTestWebhookData(data);
          setTestStatus('webhook_received');
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [showTestFlowDialog, testWebhookToken, testStatus]);

  const buildSamplePayload = useCallback(() => {
    const currentIndex = tasks.findIndex(t => String(t.id) === String(activeTaskId));
    const sampleFormData = {};
    const slugCounts = {};

    // Helper to convert field names to slugs for reusable payload keys
    const toSlug = (name) => {
      if (!name) return '';
      return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    };

    // Gather form fields from all tasks before the current flow node
    // Use friendly slugified names as keys instead of raw IDs
    for (let i = 0; i < currentIndex; i++) {
      const task = tasks[i];
      (task.content || [])
        .filter(item => item.category === 'form')
        .forEach(item => {
          const fieldName = item.content?.fieldName || item.label || item.id;
          const fieldType = item.label || '';

          // Generate a slug key from the field name
          let slug = toSlug(fieldName);
          if (!slug) slug = item.id;
          if (slugCounts[slug]) {
            slugCounts[slug]++;
            slug = `${slug}_${slugCounts[slug]}`;
          } else {
            slugCounts[slug] = 1;
          }

          // Generate realistic sample data that matches the actual field configuration
          let sampleValue;
          const options = item.content?.options || [];
          const columns = item.content?.columns || [];
          switch (fieldType) {
            case 'Short Text': sampleValue = `Example ${fieldName}`; break;
            case 'Long Text': sampleValue = `This is example text for the ${fieldName} field.`; break;
            case 'Email': sampleValue = 'john.doe@example.com'; break;
            case 'Website': sampleValue = 'https://example.com'; break;
            case 'Date': sampleValue = new Date().toISOString().split('T')[0]; break;
            case 'Numbers': sampleValue = 100; break;
            case 'Dropdown':
              // Use actual dropdown options if configured
              sampleValue = options.length > 0
                ? (options[0].value || options[0].label || 'Option A')
                : 'Option A';
              break;
            case 'Multi Choice':
              // Use actual options if configured
              sampleValue = options.length > 0
                ? options.slice(0, Math.min(2, options.length)).map(o => o.value || o.label || 'Option')
                : ['Option A', 'Option B'];
              break;
            case 'User':
              sampleValue = { id: 'user-123', first_name: 'John', last_name: 'Doe', email: 'john.doe@example.com' };
              break;
            case 'Table':
              // Use actual column names if configured
              if (columns.length > 0) {
                const row = {};
                columns.forEach(col => { row[col.name || col.id] = `Sample ${col.name || 'value'}`; });
                sampleValue = [row];
              } else {
                sampleValue = [{ 'Column 1': 'Row 1 Value', 'Column 2': 'Row 1 Value' }];
              }
              break;
            case 'Signature': sampleValue = '[signature_data_url]'; break;
            case 'File Upload': sampleValue = { name: 'document.pdf', url: 'https://example.com/document.pdf' }; break;
            default: sampleValue = `Example ${fieldName}`;
          }
          sampleFormData[slug] = sampleValue;
        });
    }

    // Generate a test webhook token for this test run
    const token = crypto.randomUUID();
    setTestWebhookToken(token);

    return {
      workflow_run_id: 'test-run-' + Date.now(),
      task_run_id: 'test-task-' + Date.now(),
      form_data: sampleFormData,
      workflow_name: workflowName || 'Untitled Workflow',
      task_name: activeTask?.name || activeTask?.formTitle || 'Flow Step',
      webhook_url: `${SUPABASE_URL}/functions/v1/process-webhook/test/${token}`,
    };
  }, [tasks, activeTaskId, workflowName, activeTask]);

  const handleOpenTestFlow = useCallback(() => {
    const payload = buildSamplePayload();
    setTestPayload(JSON.stringify(payload, null, 2));
    setTestResult(null);
    setTestWebhookData(null);
    setTestStatus('idle');
    setShowTestFlowDialog(true);
  }, [buildSamplePayload]);

  const handleSendTestFlow = useCallback(async () => {
    const flowId = activeTask?.aiFlowConfig?.flowId;
    if (!flowId || !company?.id) return;

    setTestStatus('sending');
    setTestResult(null);
    setTestWebhookData(null);

    try {
      let payload;
      try {
        payload = JSON.parse(testPayload);
      } catch {
        setTestStatus('error');
        setTestResult({ error: 'Invalid JSON in payload' });
        return;
      }

      // Fire-and-forget: trigger the flow, don't wait for the full result
      // The flow will call back to the webhook_url when it's done
      const { triggerFlow } = await import('@/lib/aiFlowsService');
      const result = await triggerFlow(company.id, flowId, payload, false);
      setTestResult(result);

      // Now wait for the webhook callback via Supabase realtime
      setTestStatus('waiting');
    } catch (err) {
      setTestStatus('error');
      setTestResult({ error: err.message || 'Test execution failed' });
    }
  }, [activeTask, company, testPayload]);

  // In company process mode, we wait for company data to load (no space to wait for)
  // In space mode, we wait for space data to load
  const isLoading = isCompanyProcessMode
    ? (isLoadingWorkflow || !company?.id || !userProfile?.id)
    : (loading || isLoadingWorkflow);

  if (isLoading) {
    return (
      <AppShell3>
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner />
        </div>
      </AppShell3>
    );
  }

  return (
    <AppShell3 pageTitle='' pageDescription=''>
      <PageTransition>
        <ToolHeader
          spaceName={isCompanyProcessMode ? 'Company Processes' : (space?.name || 'Space')}
          spaceImage={isCompanyProcessMode ? null : space?.logo_url}
          toolName={
            isEditingName ? (
              <Input
                value={workflowName}
                onChange={(e) => setWorkflowName(e.target.value)}
                onBlur={() => setIsEditingName(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setIsEditingName(false);
                  }
                }}
                className="h-7 text-sm font-semibold text-gray-700 border-primary w-[200px]"
                autoFocus
              />
            ) : (
              <button
                onClick={() => setIsEditingName(true)}
                className="text-sm font-semibold text-gray-700 hover:text-primary transition-colors"
              >
                {workflowName}
              </button>
            )
          }
          currentTool='tasks'
          spaceTools={isCompanyProcessMode ? [] : spaceTools}
          parentBreadcrumb={{
            name: isCompanyProcessMode ? 'Processes' : 'Tasks',
            onClick: () => navigate(isCompanyProcessMode ? '/processes' : `/space/${projectId}/tasks`)
          }}
          actions={
            <div className="flex items-center gap-3">
              {!isCompanyProcessMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/space/${projectId}/workflow-preview/${currentWorkflowId}`)}
                  disabled={!currentWorkflowId}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </Button>
              )}

              <Button
                size="sm"
                onClick={handlePublishWorkflow}
                disabled={!currentWorkflowId}
                className="bg-green-600 hover:bg-green-700"
              >
                <Play className="h-4 w-4 mr-2" />
                Publish
              </Button>

              <div className={`flex items-center gap-2 transition-colors ${saveStatus === 'saving' ? 'text-muted-foreground' :
                  saveStatus === 'saved' ? 'text-green-600' :
                    saveStatus === 'error' ? 'text-destructive' :
                      'text-muted-foreground'
                }`}>
                <Save className={`h-4 w-4 ${saveStatus === 'saving' ? 'animate-pulse' : ''}`} />
                <span className="text-sm">
                  {saveStatus === 'saving' ? 'Saving...' :
                    saveStatus === 'saved' ? 'Saved' :
                      saveStatus === 'error' ? 'Error saving' : 'Saved'}
                </span>
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={handlePublishWorkflow} disabled={!currentWorkflowId}>
                    <Save className="h-4 w-4 mr-2" />
                    Publish
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handlePublishAndRun} disabled={!currentWorkflowId}>
                    <Play className="h-4 w-4 mr-2" />
                    Publish & Run
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />

                  <DropdownMenuItem onClick={() => setShowSetupModal(true)}>
                    <Settings className="h-4 w-4 mr-2" />
                    {isCompanyProcessMode ? 'Process Settings' : 'Workflow Settings'}
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />

                  <DropdownMenuItem onClick={handleDeleteWorkflow} className="text-destructive">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          }
        />

        {/* Main Content */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div className="container mx-auto px-4 py-6">
            <div className="flex gap-6">
              {/* Left Sidebar - Triggers & Tasks */}
              <div className="w-80 border rounded-lg bg-card flex flex-col overflow-y-auto sticky top-6 h-[calc(100vh-120px)]">
                {/* Notification Cover Preview */}
                {workflowSettings.notificationCover && (
                  <div className="relative group">
                    {workflowSettings.notificationCover.type === 'image' ? (
                      <img
                        src={workflowSettings.notificationCover.value}
                        alt="Workflow cover"
                        className="w-full h-32 object-cover rounded-t-lg"
                      />
                    ) : (
                      <div
                        className="w-full h-32 rounded-t-lg"
                        style={{ background: workflowSettings.notificationCover.value || workflowSettings.notificationCover }}
                      />
                    )}
                    {/* Workflow Name Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center p-4 pointer-events-none">
                      <h2 className="text-white text-2xl font-bold drop-shadow-lg text-center">{workflowName}</h2>
                    </div>
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 rounded-t-lg">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setShowSetupModal(true)}
                      >
                        Change
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setWorkflowSettings(prev => ({ ...prev, notificationCover: null }));
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                )}

                <div className="p-6 flex-1">
                  {/* Triggers Section */}
                  <div className="relative pb-6 flex justify-center">
                    <div className="flex items-center gap-1 px-3 py-2 rounded-lg border bg-card relative z-10">
                      {/* Show trigger icons if any are selected */}
                      {triggers.map((trigger) => {
                        // Ensure we have a valid React component
                        const IconComponent = trigger.icon;
                        const hasValidIcon = IconComponent && (typeof IconComponent === 'function' || (typeof IconComponent === 'object' && IconComponent.$$typeof));

                        return (
                          <button
                            key={trigger.id}
                            onClick={() => handleTriggerSelect(trigger)}
                            className="p-1.5 hover:bg-muted rounded transition-colors"
                            title={trigger.label}
                          >
                            {hasValidIcon ? (
                              <IconComponent className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <Zap className="h-4 w-4 text-muted-foreground" />
                            )}
                          </button>
                        );
                      })}

                      {/* Add trigger button */}
                      <button
                        onClick={() => setShowTriggerModal(true)}
                        className="p-1.5 hover:bg-muted rounded transition-colors"
                        title="Add trigger"
                      >
                        <Plus className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </div>

                    {/* Vertical connecting line from triggers to tasks - starts from bottom of trigger box */}
                    <div className="absolute left-1/2 -translate-x-1/2 top-[42px] bottom-0 w-[2px] bg-border z-0" />
                  </div>

                  {/* Tasks Section */}
                  <div className="relative">
                    {/* Continuous background line - extends full height behind everything */}
                    <div className="absolute left-1/2 -translate-x-1/2 -top-2 bottom-0 w-[2px] bg-border z-0" />

                    <SortableContext items={tasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                      <div className="flex flex-col relative z-10">
                        <AnimatePresence initial={false}>
                          {tasks.map((task, index) => {
                            // Calculate the visual position during dragging
                            let visualIndex = index;
                            if (activeTaskDragId && overTaskId) {
                              const draggedIndex = tasks.findIndex(t => t.id === activeTaskDragId);
                              const overIndex = tasks.findIndex(t => t.id === overTaskId);

                              if (task.id === activeTaskDragId) {
                                visualIndex = overIndex;
                              } else if (draggedIndex < overIndex && index > draggedIndex && index <= overIndex) {
                                visualIndex = index - 1;
                              } else if (draggedIndex > overIndex && index >= overIndex && index < draggedIndex) {
                                visualIndex = index + 1;
                              }
                            }

                            const isFirst = visualIndex === 0;
                            const isLast = visualIndex === tasks.length - 1;

                            return (
                              <motion.div
                                key={task.id}
                                variants={taskItemVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                className="w-full"
                              >
                                {/* Spacer before task (except first) */}
                                {!isFirst && <TaskSpacer />}

                                {/* Task Card */}
                                <SortableTaskItem
                                  task={task}
                                  index={visualIndex}
                                  isActive={activeTaskId === task.id}
                                  isLast={isLast}
                                  isFirst={isFirst}
                                  isOnlyOne={tasks.length === 1}
                                  totalTasks={tasks.length}
                                  onTaskClick={() => setActiveTaskId(task.id)}
                                  onTaskChange={(newName) => {
                                    const taskIndex = tasks.findIndex(t => t.id === task.id);
                                    const newTasks = [...tasks];
                                    newTasks[taskIndex].name = newName;
                                    newTasks[taskIndex].formTitle = newName;
                                    setTasks(newTasks);
                                  }}
                                  onTaskFocus={() => setActiveTaskId(task.id)}
                                  onDuplicate={handleDuplicateTask}
                                  onDelete={handleDeleteTask}
                                />
                              </motion.div>
                            );
                          })}
                        </AnimatePresence>
                      </div>
                    </SortableContext>

                    {/* Add Task/Flow Button */}
                    <div className="relative mt-6 z-10">
                      <div className="flex items-center justify-center relative z-50">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <motion.button
                              className="w-[30px] h-[30px] rounded-full border border-border bg-white shadow-sm flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary hover:shadow-md"
                              whileHover={{ scale: 1.15 }}
                              whileTap={{ scale: 0.95 }}
                              transition={{ type: "spring", stiffness: 400, damping: 17 }}
                            >
                              <Plus className="h-4 w-4" />
                            </motion.button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="center">
                            <DropdownMenuItem onClick={() => addTask('task')}>
                              <FileText className="h-4 w-4 mr-2" />
                              Add Page
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => addTask('flow')}>
                              <Zap className="h-4 w-4 mr-2 text-blue-500" />
                              Add AI Flow
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => addTask('end')}
                              disabled={hasEndScreen}
                            >
                              <FlagTriangleRight className="h-4 w-4 mr-2 text-emerald-500" />
                              {hasEndScreen ? 'End Screen exists' : 'Add End Screen'}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Center Canvas */}
              {isActiveTaskFlow ? (
                /* Flow Node Configuration Panel */
                <div className="flex-1 max-w-3xl bg-white rounded-2xl shadow-sm border overflow-auto min-h-[600px]">
                  <div className="p-8">
                    {/* Flow Header */}
                    <div className="flex items-center gap-4 mb-8">
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                        <Zap className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <Input
                          value={activeTask?.formTitle || activeTask?.name || ''}
                          onChange={(e) => updateFormTitle(e.target.value)}
                          placeholder="Flow Step Name"
                          className="text-2xl font-bold border-0 px-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 h-auto"
                        />
                        <p className="text-sm text-muted-foreground">
                          This step triggers an AI Flow automation
                        </p>
                      </div>
                    </div>

                    {/* Flow Configuration */}
                    <div className="space-y-6">
                      {/* AI Flow Selection */}
                      <div className="p-6 border rounded-xl bg-blue-50/50">
                        <h3 className="font-semibold mb-4 flex items-center gap-2">
                          <Zap className="h-4 w-4 text-blue-500" />
                          AI Flow Configuration
                        </h3>

                        {activeTask?.aiFlowConfig?.flowId ? (
                          <div className="space-y-4">
                            <div className="p-4 bg-white rounded-lg border">
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="font-medium">{activeTask.aiFlowConfig.flowName}</div>
                                  <div className="text-sm text-muted-foreground">
                                    {activeTask.aiFlowConfig.mustWait
                                      ? 'Blocking: User waits for completion'
                                      : 'Non-blocking: Runs in background'}
                                  </div>
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setShowAiFlowModal(true)}
                                >
                                  Change
                                </Button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <Button
                            onClick={() => setShowAiFlowModal(true)}
                            className="w-full bg-blue-600 hover:bg-blue-700"
                          >
                            <Zap className="h-4 w-4 mr-2" />
                            Select AI Flow
                          </Button>
                        )}
                      </div>

                      {/* Flow Options */}
                      <div className="p-6 border rounded-xl">
                        <h3 className="font-semibold mb-4">Flow Options</h3>
                        <div className="space-y-4">
                          {/* Must Wait Toggle */}
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium">Must Wait</div>
                              <div className="text-sm text-muted-foreground">
                                User sees a loading screen until the flow completes
                              </div>
                            </div>
                            <Button
                              variant={activeTask?.aiFlowConfig?.mustWait ? "default" : "outline"}
                              size="sm"
                              onClick={() => {
                                const newConfig = {
                                  ...activeTask?.aiFlowConfig,
                                  mustWait: !activeTask?.aiFlowConfig?.mustWait
                                };
                                handleSaveAiFlowConfig(newConfig);
                              }}
                              disabled={!activeTask?.aiFlowConfig?.flowId}
                            >
                              {activeTask?.aiFlowConfig?.mustWait ? 'Enabled' : 'Disabled'}
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Webhook Callback URL */}
                      {currentWorkflowId && activeTask?.id && (
                        <div className="p-6 border rounded-xl">
                          <h3 className="font-semibold mb-2 flex items-center gap-2">
                            <Webhook className="h-4 w-4 text-blue-500" />
                            Webhook Callback
                          </h3>
                          <p className="text-sm text-muted-foreground mb-3">
                            When this flow is triggered, a <code className="bg-muted px-1 py-0.5 rounded text-xs">webhook_url</code> is automatically included in the payload sent to your external system. POST data back to that URL to complete this step and pass data into the process.
                          </p>
                          <div className="space-y-3">
                            <div>
                              <label className="text-xs font-medium text-muted-foreground mb-1 block">URL Pattern</label>
                              <div className="flex gap-2">
                                <input
                                  value={`https://yqmlqctpakqxtwzrdofl.supabase.co/functions/v1/process-webhook/${currentWorkflowId}/<run_id>/${activeTask.id}`}
                                  readOnly
                                  className="flex-1 font-mono text-[11px] bg-muted rounded-md border px-3 py-2 text-muted-foreground"
                                />
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="shrink-0"
                                  onClick={async () => {
                                    const url = `https://yqmlqctpakqxtwzrdofl.supabase.co/functions/v1/process-webhook/${currentWorkflowId}/<run_id>/${activeTask.id}`;
                                    try {
                                      if (navigator.clipboard && navigator.clipboard.writeText) {
                                        await navigator.clipboard.writeText(url);
                                      } else {
                                        const textArea = document.createElement('textarea');
                                        textArea.value = url;
                                        textArea.style.position = 'fixed';
                                        textArea.style.left = '-999999px';
                                        textArea.style.top = '-999999px';
                                        document.body.appendChild(textArea);
                                        textArea.focus();
                                        textArea.select();
                                        document.execCommand('copy');
                                        document.body.removeChild(textArea);
                                      }
                                      toast.success('Webhook URL pattern copied to clipboard');
                                    } catch (error) {
                                      console.error('Failed to copy:', error);
                                      toast.error('Failed to copy URL');
                                    }
                                  }}
                                  title="Copy URL pattern"
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-lg space-y-2">
                              <p className="font-medium">How it works:</p>
                              <ul className="list-disc list-inside space-y-1">
                                <li>The full <code className="bg-muted px-1 py-0.5 rounded">webhook_url</code> (with actual run ID) is sent in the trigger payload</li>
                                <li>Your external system POSTs JSON data back to that URL when ready</li>
                                <li>The process step completes and the data is available via <code className="bg-muted px-1 py-0.5 rounded">{'{{ webhook.* }}'}</code> variables</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Test Flow & Webhook */}
                      {activeTask?.aiFlowConfig?.flowId && currentWorkflowId && (
                        <div className="p-6 border rounded-xl border-dashed border-blue-300 bg-blue-50/30">
                          <h3 className="font-semibold mb-2 flex items-center gap-2">
                            <Send className="h-4 w-4 text-blue-500" />
                            Test Flow
                          </h3>
                          <p className="text-sm text-muted-foreground mb-4">
                            Send a test payload to your flow. The payload includes a test <code className="bg-muted px-1 py-0.5 rounded text-xs">webhook_url</code> that you can call back to verify the round-trip.
                          </p>
                          <Button
                            variant="outline"
                            className="w-full"
                            onClick={handleOpenTestFlow}
                          >
                            <Play className="h-4 w-4 mr-2" />
                            Send Test Payload
                          </Button>
                        </div>
                      )}

                      {/* Test Flow (no webhook - flow only) */}
                      {activeTask?.aiFlowConfig?.flowId && !currentWorkflowId && (
                        <div className="p-6 border rounded-xl border-dashed border-blue-300 bg-blue-50/30">
                          <h3 className="font-semibold mb-2 flex items-center gap-2">
                            <Send className="h-4 w-4 text-blue-500" />
                            Test Flow
                          </h3>
                          <p className="text-sm text-muted-foreground mb-4">
                            Save the process first to enable webhook testing.
                          </p>
                          <Button
                            variant="outline"
                            className="w-full"
                            onClick={handleOpenTestFlow}
                          >
                            <Play className="h-4 w-4 mr-2" />
                            Send Test Payload
                          </Button>
                        </div>
                      )}

                      {/* Help Text */}
                      <div className="p-4 bg-muted/50 rounded-lg text-sm text-muted-foreground">
                        <p className="font-medium mb-2">How Flow Steps Work:</p>
                        <ul className="list-disc list-inside space-y-1">
                          <li>When the previous page is completed, this flow is triggered</li>
                          <li>Data from previous pages is sent to the flow as variables</li>
                          <li>A <code className="bg-muted px-1 py-0.5 rounded text-xs">webhook_url</code> is included so external systems can call back</li>
                          <li>If "Must Wait" is enabled, the user sees a loading screen until the webhook is received or the flow completes</li>
                          <li>Flow output is available via <code className="bg-muted px-1 py-0.5 rounded text-xs">{'{{ flow.* }}'}</code> and webhook data via <code className="bg-muted px-1 py-0.5 rounded text-xs">{'{{ webhook.* }}'}</code> variables</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ) : isActiveTaskEnd ? (
                /* End Screen Configuration Panel - uses DroppableCanvas for content, plus action buttons */
                <div className="flex-1 max-w-3xl flex flex-col gap-0">
                  <DroppableCanvas
                    activeTaskContent={activeTaskContent}
                    background={workflowSettings.background}
                    formTitle={activeTask?.formTitle}
                    onFormTitleChange={updateFormTitle}
                    isEndScreen
                  >
                    <SortableContext items={activeTaskContent.map(item => item.id)} strategy={verticalListSortingStrategy}>
                      {activeTaskContent.map((item, index) => (
                        <React.Fragment key={item.id}>
                          <SortableCanvasItem
                            item={item}
                            onRemove={removeContentItem}
                            onDuplicate={duplicateContentItem}
                            onUpdateContent={updateContentItem}
                            projectId={projectId}
                            taskId={activeTaskId}
                            companyId={company?.id}
                            showDropIndicator={dragOverItemId === item.id && activeDragItem?.type === 'new-item'}
                            dropIndicatorPosition={dragOverPosition}
                            tasks={tasks}
                            currentTaskId={activeTaskId}
                            onUpdateConditionalLogic={updateConditionalLogic}
                          />
                        </React.Fragment>
                      ))}
                    </SortableContext>
                    <DropPlaceholder />

                    {/* Action Buttons Section */}
                    <div className="mt-6 pt-6 border-t border-dashed border-emerald-300">
                      <div className="flex items-center gap-2 mb-4">
                        <FlagTriangleRight className="h-4 w-4 text-emerald-600" />
                        <h3 className="text-sm font-semibold text-emerald-700">Action Buttons</h3>
                      </div>
                      <p className="text-xs text-muted-foreground mb-3">
                        Add buttons for downloads or links. Use variables in URLs to reference flow output data.
                      </p>
                      <div className="space-y-3">
                        {(activeTask?.actionButtons || []).map((btn, btnIndex) => (
                          <div key={btn.id} className="p-3 border rounded-lg bg-white space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-muted-foreground">Button {btnIndex + 1}</span>
                              <button
                                onClick={() => {
                                  const newButtons = (activeTask.actionButtons || []).filter(b => b.id !== btn.id);
                                  const updatedTasks = tasks.map(t =>
                                    t.id === activeTaskId ? { ...t, actionButtons: newButtons } : t
                                  );
                                  setTasks(updatedTasks);
                                }}
                                className="text-muted-foreground hover:text-destructive transition-colors"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            <Input
                              placeholder="Button label (e.g. Download Contract)"
                              value={btn.label || ''}
                              onChange={(e) => {
                                const newButtons = [...(activeTask.actionButtons || [])];
                                newButtons[btnIndex] = { ...btn, label: e.target.value };
                                const updatedTasks = tasks.map(t =>
                                  t.id === activeTaskId ? { ...t, actionButtons: newButtons } : t
                                );
                                setTasks(updatedTasks);
                              }}
                              className="h-8 text-sm"
                            />
                            <Input
                              placeholder="URL or variable (e.g. {{ flow.files.0 }})"
                              value={btn.url || ''}
                              onChange={(e) => {
                                const newButtons = [...(activeTask.actionButtons || [])];
                                newButtons[btnIndex] = { ...btn, url: e.target.value };
                                const updatedTasks = tasks.map(t =>
                                  t.id === activeTaskId ? { ...t, actionButtons: newButtons } : t
                                );
                                setTasks(updatedTasks);
                              }}
                              className="h-8 text-sm font-mono"
                            />
                            <div className="flex items-center gap-2">
                              <select
                                value={btn.style || 'primary'}
                                onChange={(e) => {
                                  const newButtons = [...(activeTask.actionButtons || [])];
                                  newButtons[btnIndex] = { ...btn, style: e.target.value };
                                  const updatedTasks = tasks.map(t =>
                                    t.id === activeTaskId ? { ...t, actionButtons: newButtons } : t
                                  );
                                  setTasks(updatedTasks);
                                }}
                                className="h-8 text-xs border rounded-md px-2 bg-background"
                              >
                                <option value="primary">Primary (filled)</option>
                                <option value="outline">Outline</option>
                              </select>
                              <select
                                value={btn.icon || 'download'}
                                onChange={(e) => {
                                  const newButtons = [...(activeTask.actionButtons || [])];
                                  newButtons[btnIndex] = { ...btn, icon: e.target.value };
                                  const updatedTasks = tasks.map(t =>
                                    t.id === activeTaskId ? { ...t, actionButtons: newButtons } : t
                                  );
                                  setTasks(updatedTasks);
                                }}
                                className="h-8 text-xs border rounded-md px-2 bg-background"
                              >
                                <option value="download">Download icon</option>
                                <option value="external">External link icon</option>
                                <option value="none">No icon</option>
                              </select>
                            </div>
                          </div>
                        ))}
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full border-dashed border-emerald-300 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"
                          onClick={() => {
                            const newButton = {
                              id: `btn-${Date.now()}`,
                              label: '',
                              url: '',
                              style: 'primary',
                              icon: 'download',
                            };
                            const updatedTasks = tasks.map(t =>
                              t.id === activeTaskId
                                ? { ...t, actionButtons: [...(t.actionButtons || []), newButton] }
                                : t
                            );
                            setTasks(updatedTasks);
                          }}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Action Button
                        </Button>
                      </div>
                    </div>
                  </DroppableCanvas>
                </div>
              ) : (
                <DroppableCanvas
                  activeTaskContent={activeTaskContent}
                  background={workflowSettings.background}
                  formTitle={activeTask?.formTitle}
                  onFormTitleChange={updateFormTitle}
                  onOpenAssignment={() => setShowAssignmentModal(true)}
                  assigneeCount={assigneeCount}
                  onOpenDueDate={() => setShowDueDateModal(true)}
                  hasDueDate={hasDueDate}
                  onToggleStop={handleToggleStop}
                  hasStopEnabled={hasStopEnabled}
                  tasks={tasks}
                  currentTaskId={activeTaskId}
                  taskConditionalLogic={activeTask?.conditionalLogic}
                  onUpdateTaskConditionalLogic={updateTaskConditionalLogic}
                >
                  <SortableContext items={activeTaskContent.map(item => item.id)} strategy={verticalListSortingStrategy}>
                    {activeTaskContent.map((item, index) => (
                      <React.Fragment key={item.id}>
                        <SortableCanvasItem
                          item={item}
                          onRemove={removeContentItem}
                          onDuplicate={duplicateContentItem}
                          onUpdateContent={updateContentItem}
                          projectId={projectId}
                          taskId={activeTaskId}
                          companyId={company?.id}
                          showDropIndicator={dragOverItemId === item.id && activeDragItem?.type === 'new-item'}
                          tasks={tasks}
                          currentTaskId={activeTaskId}
                          onUpdateConditionalLogic={updateConditionalLogic}
                        />
                      </React.Fragment>
                    ))}
                  </SortableContext>
                  <DropPlaceholder />

                  {/* Action Buttons Section */}
                  <div className="mt-6 pt-6 border-t border-dashed border-blue-300">
                    <div className="flex items-center gap-2 mb-4">
                      <ExternalLink className="h-4 w-4 text-blue-600" />
                      <h3 className="text-sm font-semibold text-blue-700">Action Buttons</h3>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">
                      Add buttons for downloads or links. Use variables in URLs to reference data from previous steps.
                    </p>
                    <div className="space-y-3">
                      {(activeTask?.actionButtons || []).map((btn, btnIndex) => (
                        <div key={btn.id} className="p-3 border rounded-lg bg-white space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-muted-foreground">Button {btnIndex + 1}</span>
                            <button
                              onClick={() => {
                                const newButtons = (activeTask.actionButtons || []).filter(b => b.id !== btn.id);
                                const updatedTasks = tasks.map(t =>
                                  t.id === activeTaskId ? { ...t, actionButtons: newButtons } : t
                                );
                                setTasks(updatedTasks);
                              }}
                              className="text-muted-foreground hover:text-destructive transition-colors"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <Input
                            placeholder="Button label (e.g. Download Contract)"
                            value={btn.label || ''}
                            onChange={(e) => {
                              const newButtons = [...(activeTask.actionButtons || [])];
                              newButtons[btnIndex] = { ...btn, label: e.target.value };
                              const updatedTasks = tasks.map(t =>
                                t.id === activeTaskId ? { ...t, actionButtons: newButtons } : t
                              );
                              setTasks(updatedTasks);
                            }}
                            className="h-8 text-sm"
                          />
                          <Input
                            placeholder="URL or variable (e.g. {{ webhook.document_url }})"
                            value={btn.url || ''}
                            onChange={(e) => {
                              const newButtons = [...(activeTask.actionButtons || [])];
                              newButtons[btnIndex] = { ...btn, url: e.target.value };
                              const updatedTasks = tasks.map(t =>
                                t.id === activeTaskId ? { ...t, actionButtons: newButtons } : t
                              );
                              setTasks(updatedTasks);
                            }}
                            className="h-8 text-sm font-mono"
                          />
                          <div className="flex items-center gap-2">
                            <select
                              value={btn.style || 'primary'}
                              onChange={(e) => {
                                const newButtons = [...(activeTask.actionButtons || [])];
                                newButtons[btnIndex] = { ...btn, style: e.target.value };
                                const updatedTasks = tasks.map(t =>
                                  t.id === activeTaskId ? { ...t, actionButtons: newButtons } : t
                                );
                                setTasks(updatedTasks);
                              }}
                              className="h-8 text-xs border rounded-md px-2 bg-background"
                            >
                              <option value="primary">Primary (filled)</option>
                              <option value="outline">Outline</option>
                            </select>
                            <select
                              value={btn.icon || 'download'}
                              onChange={(e) => {
                                const newButtons = [...(activeTask.actionButtons || [])];
                                newButtons[btnIndex] = { ...btn, icon: e.target.value };
                                const updatedTasks = tasks.map(t =>
                                  t.id === activeTaskId ? { ...t, actionButtons: newButtons } : t
                                );
                                setTasks(updatedTasks);
                              }}
                              className="h-8 text-xs border rounded-md px-2 bg-background"
                            >
                              <option value="download">Download icon</option>
                              <option value="external">External link icon</option>
                              <option value="none">No icon</option>
                            </select>
                          </div>
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full border-dashed border-blue-300 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                        onClick={() => {
                          const newButton = {
                            id: `btn-${Date.now()}`,
                            label: '',
                            url: '',
                            style: 'primary',
                            icon: 'download',
                          };
                          const updatedTasks = tasks.map(t =>
                            t.id === activeTaskId
                              ? { ...t, actionButtons: [...(t.actionButtons || []), newButton] }
                              : t
                          );
                          setTasks(updatedTasks);
                        }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Action Button
                      </Button>
                    </div>
                  </div>
                </DroppableCanvas>
              )}

              {/* Right Sidebar - Content/Variables & Form Fields (hidden for flow and end nodes) */}
              {!isActiveTaskFlow && !isActiveTaskEnd && (
              <div className="w-64 border rounded-lg bg-card overflow-y-auto sticky top-6 h-[calc(100vh-120px)]">
                {/* Tab Switcher - Content / Variables */}
                <div className="p-2 border-b">
                  <div className="flex rounded-lg bg-muted p-0.5">
                    <button
                      onClick={() => setSidebarTab('content')}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        sidebarTab === 'content'
                          ? 'bg-background text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <FileText className="h-3 w-3" />
                      Content
                    </button>
                    <button
                      onClick={() => setSidebarTab('variables')}
                      disabled={availableVariables.length === 0}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        sidebarTab === 'variables'
                          ? 'bg-background text-foreground shadow-sm'
                          : availableVariables.length === 0
                            ? 'text-muted-foreground/50 cursor-not-allowed'
                            : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Variable className="h-3 w-3" />
                      Variables
                      {availableVariables.length > 0 && (
                        <span className="ml-0.5 px-1.5 py-0.5 text-[10px] rounded-full bg-purple-100 text-purple-700">
                          {availableVariables.length}
                        </span>
                      )}
                    </button>
                  </div>
                </div>

                {/* Content Tab */}
                {sidebarTab === 'content' && (
                  <div className="p-4 border-b">
                    <div className="space-y-1">
                      {contentItems.map((item) => (
                        <DraggableFieldItem
                          key={item.label}
                          item={item}
                          category="content"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Variables Tab */}
                {sidebarTab === 'variables' && availableVariables.length > 0 && (
                  <div className="p-4 border-b">
                    <p className="text-[10px] text-muted-foreground mb-3">
                      Click to copy merge tag. Flow payload key shown in <code className="bg-muted px-1 rounded">mono</code>.
                    </p>
                    <div className="space-y-1">
                      {availableVariables.map((variable) => (
                        <button
                          key={variable.id}
                          onClick={() => {
                            navigator.clipboard.writeText(variable.variableKey);
                            toast.success(`Copied "${variable.fieldName}" - paste into any text block`);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-left rounded-md border border-transparent hover:border-purple-200 hover:bg-purple-50 transition-colors group"
                        >
                          {variable.isWebhook
                            ? <Webhook className="h-3.5 w-3.5 text-purple-500 flex-shrink-0" />
                            : <Variable className="h-3.5 w-3.5 text-purple-500 flex-shrink-0" />
                          }
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-700 truncate group-hover:text-purple-700">
                              {variable.fieldName}
                            </div>
                            <div className="text-[10px] text-muted-foreground truncate">
                              <code className="bg-muted px-1 rounded">{variable.payloadKey}</code> · {variable.isWebhook ? 'Webhook' : `Step ${variable.taskIndex}`}
                            </div>
                          </div>
                          <Copy className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Form Fields Section - Always visible */}
                <div className="p-4">
                  <h3 className="text-xs font-semibold text-muted-foreground mb-3 uppercase">
                    Form Fields
                  </h3>
                  <div className="space-y-1">
                    {formFields.map((item) => (
                      <DraggableFieldItem
                        key={item.label}
                        item={item}
                        category="form"
                      />
                    ))}
                  </div>
                </div>
              </div>
              )}

              {/* Right Sidebar for End Screen - Content & Variables only (no form fields) */}
              {isActiveTaskEnd && (
              <div className="w-64 border rounded-lg bg-card overflow-y-auto sticky top-6 h-[calc(100vh-120px)]">
                <div className="p-2 border-b">
                  <div className="flex rounded-lg bg-muted p-0.5">
                    <button
                      onClick={() => setSidebarTab('content')}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        sidebarTab === 'content'
                          ? 'bg-background text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <FileText className="h-3 w-3" />
                      Content
                    </button>
                    <button
                      onClick={() => setSidebarTab('variables')}
                      disabled={availableVariables.length === 0}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        sidebarTab === 'variables'
                          ? 'bg-background text-foreground shadow-sm'
                          : availableVariables.length === 0
                            ? 'text-muted-foreground/50 cursor-not-allowed'
                            : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Variable className="h-3 w-3" />
                      Variables
                      {availableVariables.length > 0 && (
                        <span className="ml-0.5 px-1.5 py-0.5 text-[10px] rounded-full bg-purple-100 text-purple-700">
                          {availableVariables.length}
                        </span>
                      )}
                    </button>
                  </div>
                </div>

                {sidebarTab === 'content' && (
                  <div className="p-4">
                    <p className="text-[10px] text-muted-foreground mb-3">
                      Drag content blocks onto the end screen
                    </p>
                    <div className="space-y-1">
                      {contentItems.filter(item => item.label !== 'Subtasks').map((item) => (
                        <DraggableFieldItem
                          key={item.label}
                          item={item}
                          category="content"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {sidebarTab === 'variables' && availableVariables.length > 0 && (
                  <div className="p-4">
                    <p className="text-[10px] text-muted-foreground mb-3">
                      Click to copy merge tag. Flow payload key shown in <code className="bg-muted px-1 rounded">mono</code>.
                    </p>
                    <div className="space-y-1">
                      {availableVariables.map((variable) => (
                        <button
                          key={variable.id}
                          onClick={() => {
                            navigator.clipboard.writeText(variable.variableKey);
                            toast.success(`Copied "${variable.fieldName}" - paste into any text block or button URL`);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-left rounded-md border border-transparent hover:border-purple-200 hover:bg-purple-50 transition-colors group"
                        >
                          {variable.isWebhook
                            ? <Webhook className="h-3.5 w-3.5 text-purple-500 flex-shrink-0" />
                            : <Variable className="h-3.5 w-3.5 text-purple-500 flex-shrink-0" />
                          }
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-700 truncate group-hover:text-purple-700">
                              {variable.fieldName}
                            </div>
                            <div className="text-[10px] text-muted-foreground truncate">
                              <code className="bg-muted px-1 rounded">{variable.payloadKey}</code> · {variable.isWebhook ? 'Webhook' : `Step ${variable.taskIndex}`}
                            </div>
                          </div>
                          <Copy className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              )}
            </div>
          </div>

          <DragOverlay dropAnimation={null}>
            {activeDragItem && activeDragItem.type === 'new-item' ? (
              <div className="flex items-center gap-2 px-4 py-3 bg-card border-2 border-primary rounded-lg shadow-lg opacity-90">
                {activeDragItem.icon && (
                  <activeDragItem.icon className={`h-4 w-4 ${activeDragItem.color || 'text-foreground'}`} />
                )}
                <span className="font-medium">{activeDragItem.label || activeDragItem.itemType}</span>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>

        {/* Workflow Setup Modal */}
        <WorkflowSetupModal
          open={showSetupModal}
          onOpenChange={setShowSetupModal}
          onSave={handleSetupSave}
          projectId={isCompanyProcessMode ? `company-${company?.id}` : projectId}
          workflowId={currentWorkflowId}
          isCompanyProcess={isCompanyProcessMode}
          userProfile={userProfile}
          webhookVariables={workflowSettings.webhookVariables}
          initialData={currentWorkflowId ? {
            workflowName: workflowName,
            description: workflowDescription,
            runName: workflowSettings.runName || '{{ current_user.name }} - {{ current_date.full }}',
            dueDate: workflowSettings.dueDate || null,
            runComments: workflowSettings.runComments || false,
            shareLinks: workflowSettings.shareLinks || false,
            showSetupAtStart: workflowSettings.showSetupAtStart || false,
            background: workflowSettings.background,
            notificationCover: workflowSettings.notificationCover,
            icon: workflowSettings.icon
          } : null}
        />

        {/* Trigger Selection Modal */}
        <TriggerSelectionModal
          open={showTriggerModal}
          onOpenChange={setShowTriggerModal}
          onSelectTrigger={handleTriggerSelect}
          selectedTriggers={triggers}
          workflowId={currentWorkflowId}
          webhookToken={webhookToken}
          onRegenerateToken={setWebhookToken}
          workflowSettings={workflowSettings}
          onSaveWebhookVariables={(variables) => {
            setWorkflowSettings({ ...workflowSettings, webhookVariables: variables });
          }}
        />

        {/* Assignment Modal */}
        <AssignmentModal
          open={showAssignmentModal}
          onOpenChange={setShowAssignmentModal}
          projectId={projectId}
          task={activeTask}
          onSaveAssignees={handleSaveAssignees}
          webhookVariables={workflowSettings?.webhookVariables}
        />

        {/* Due Date Modal */}
        <DueDateModal
          open={showDueDateModal}
          onOpenChange={setShowDueDateModal}
          task={activeTask}
          onSaveDueDate={handleSaveDueDate}
        />

        {/* AI Flow Modal */}
        <AiFlowModal
          open={showAiFlowModal}
          onOpenChange={setShowAiFlowModal}
          aiFlowConfig={activeTask?.aiFlowConfig}
          onSave={handleSaveAiFlowConfig}
          companyId={company?.id}
        />

        {/* Test Flow Dialog */}
        <Dialog open={showTestFlowDialog} onOpenChange={setShowTestFlowDialog}>
          <DialogContent className="sm:max-w-[700px] max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="h-5 w-5 text-blue-500" />
                Test Flow: {activeTask?.aiFlowConfig?.flowName}
              </DialogTitle>
              <DialogDescription>
                Edit the sample payload below and send it to test your flow. This sends real data via the API.
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto space-y-4 py-2">
              {/* Payload Editor */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">input_data payload</label>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => {
                      navigator.clipboard.writeText(testPayload);
                      toast.success('Payload copied to clipboard');
                    }}
                  >
                    <ClipboardCopy className="h-3 w-3 mr-1" />
                    Copy
                  </Button>
                </div>
                <Textarea
                  value={testPayload}
                  onChange={(e) => setTestPayload(e.target.value)}
                  className="font-mono text-xs min-h-[250px] resize-y"
                  disabled={testStatus === 'sending'}
                />
              </div>

              {/* Flow Result */}
              {testResult && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    {testStatus === 'error' ? (
                      <XCircle className="h-4 w-4 text-red-500" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    )}
                    <label className="text-sm font-medium">
                      {testStatus === 'error' ? 'Error' : 'Flow Response'}
                    </label>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs ml-auto"
                      onClick={() => {
                        navigator.clipboard.writeText(JSON.stringify(testResult, null, 2));
                        toast.success('Response copied to clipboard');
                      }}
                    >
                      <ClipboardCopy className="h-3 w-3 mr-1" />
                      Copy
                    </Button>
                  </div>
                  <pre className={cn(
                    "p-3 rounded-lg text-xs font-mono overflow-auto max-h-[200px] whitespace-pre-wrap",
                    testStatus === 'error'
                      ? "bg-red-50 border border-red-200 text-red-900"
                      : "bg-green-50 border border-green-200 text-green-900"
                  )}>
                    {JSON.stringify(testResult, null, 2)}
                  </pre>
                </div>
              )}

              {/* Webhook Callback Listener */}
              {testStatus === 'waiting' && (
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-900">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Waiting for webhook callback...
                  </div>
                  <p className="text-xs mt-1 text-blue-700">
                    Flow triggered. Listening for your external system to POST back to the test webhook URL via Supabase Realtime.
                  </p>
                </div>
              )}

              {/* Webhook Callback Data */}
              {testWebhookData && (() => {
                // Flatten webhook data to show available variables
                const flatVars = [];
                const flatten = (obj, prefix) => {
                  if (obj === null || obj === undefined) return;
                  if (Array.isArray(obj)) {
                    obj.forEach((item, index) => flatten(item, `${prefix}.${index}`));
                    if (obj.length > 0) flatVars.push({ key: prefix, sampleValue: String(obj[0]) });
                    return;
                  }
                  if (typeof obj === 'object') {
                    Object.entries(obj).forEach(([key, value]) => flatten(value, `${prefix}.${key}`));
                    return;
                  }
                  flatVars.push({ key: prefix, sampleValue: String(obj) });
                };
                flatten(testWebhookData, 'webhook');

                return (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Webhook className="h-4 w-4 text-purple-500" />
                      <label className="text-sm font-medium text-purple-700">Webhook Callback Received</label>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs ml-auto"
                        onClick={() => {
                          navigator.clipboard.writeText(JSON.stringify(testWebhookData, null, 2));
                          toast.success('Webhook data copied to clipboard');
                        }}
                      >
                        <ClipboardCopy className="h-3 w-3 mr-1" />
                        Copy
                      </Button>
                    </div>
                    <pre className="p-3 rounded-lg text-xs font-mono overflow-auto max-h-[200px] whitespace-pre-wrap bg-purple-50 border border-purple-200 text-purple-900">
                      {JSON.stringify(testWebhookData, null, 2)}
                    </pre>

                    {/* Variable Preview */}
                    {flatVars.length > 0 && (
                      <div className="p-3 rounded-lg bg-purple-50/50 border border-purple-200">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-medium text-purple-700 flex items-center gap-1.5">
                            <Variable className="h-3 w-3" />
                            Output Variables ({flatVars.length})
                          </p>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-purple-700 hover:text-purple-900 hover:bg-purple-100"
                            onClick={() => {
                              const updatedTasks = tasks.map(task =>
                                task.id === activeTaskId
                                  ? {
                                      ...task,
                                      aiFlowConfig: {
                                        ...task.aiFlowConfig,
                                        expectedOutputVars: flatVars,
                                      },
                                    }
                                  : task
                              );
                              setTasks(updatedTasks);
                              toast.success(`Saved ${flatVars.length} output variables — they'll appear in the variable selector on subsequent pages`);
                            }}
                          >
                            <Save className="h-3 w-3 mr-1" />
                            Save Variables
                          </Button>
                        </div>
                        <div className="space-y-1">
                          {flatVars.map((v) => (
                            <button
                              key={v.key}
                              onClick={() => {
                                navigator.clipboard.writeText(`{{ ${v.key} }}`);
                                toast.success(`Copied {{ ${v.key} }}`);
                              }}
                              className="w-full flex items-center gap-2 px-2 py-1.5 text-left rounded hover:bg-purple-100 transition-colors group"
                            >
                              <code className="text-[10px] font-mono text-purple-700 flex-shrink-0">{`{{ ${v.key} }}`}</code>
                              <span className="text-[10px] text-purple-400 mx-1">&rarr;</span>
                              <span className="text-[10px] text-purple-600 truncate flex-1">{v.sampleValue}</span>
                              <Copy className="h-2.5 w-2.5 text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setShowTestFlowDialog(false)}>
                Close
              </Button>
              <Button
                onClick={handleSendTestFlow}
                disabled={testStatus === 'sending' || testStatus === 'waiting'}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {testStatus === 'sending' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Triggering Flow...
                  </>
                ) : testStatus === 'waiting' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Waiting for callback...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Send Test
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Task</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this task? This will permanently remove the task and all its content. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={confirmDeleteTask}>
                Delete Task
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageTransition>
    </AppShell3>
  );
}
